// src/components/dashboard/Dashboard.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  TelcoFair · Command Deck
//  Aesthetic: "TelcoFair Glass Workspace" — cool workspace canvas, TelcoFair blue accents,
//  frosted floating panes with specular rim-light and pointer-reactive sheen.
//  Layout: pure CSS Grid only (no MUI <Grid>) to avoid grid-in-grid collisions.
// ─────────────────────────────────────────────────────────────────────────────

import React, {
    useState,
    useEffect,
    useRef,
    useMemo,
    useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Button,
    Avatar,
    Chip,
    IconButton,
    Skeleton,
    Badge,
    Dialog,
    DialogContent,
    TextField,
    Fab,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Autocomplete,
    LinearProgress,
    Tooltip,
    Fade,
} from '@mui/material';
import {
    Check as CheckIcon,
    Link as LinkIcon,
    Event as EventIcon,
    People as PeopleIcon,
    Business as BusinessIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Refresh as RefreshIcon,
    Add as AddIcon,
    CalendarToday as CalendarIcon,
    Schedule as ScheduleIcon,
    ArrowForward as ArrowForwardIcon,
    FiberManualRecord as LiveIcon,
    Analytics as AnalyticsIcon,
    NotificationsActive as NotificationIcon,
    RocketLaunch as RocketIcon,
    Close as CloseIcon,
    Storefront as StorefrontIcon,
    EmojiEvents as TrophyIcon,
    Search as SearchIcon,
    Videocam as VideocamIcon,
    SupportAgent as SupportAgentIcon,
    Send as SendIcon,
    Group as GroupIcon,
    Timer as TimerIcon,
    Warning as WarningIcon,
    Delete as DeleteIcon,
    ImageOutlined as ImageOutlinedIcon,
    TaskAlt as TaskAltIcon,
    PaidOutlined as PaidIcon,
    PersonAdd as PersonAddIcon,
    ReceiptLong as ReceiptLongIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { db, storage } from '../../config/firebase';
import {
    collection,
    getDocs,
    query,
    orderBy,
    where,
    onSnapshot,
    getCountFromServer,
    limit,
    documentId,
    Timestamp,
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import { uploadAdminFile } from '../../services/firebase/storage';
import { sendPlatformNotification } from '../../services/api/platformIntegrationService';

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
    Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    ChartTooltip,
    Legend,
    Filler
);

/* ═══════════════════════════════════════════════════════════════════════════
   1 · DESIGN TOKENS
   ═══════════════════════════════════════════════════════════════════════════ */

const palette = {
    canvas: '#F2F7F9',
    canvasDeep: '#E7F0F4',
    paper: '#FFFFFF',
    ink: '#182235',
    inkSoft: '#405166',
    inkLight: '#5F7184',
    inkFaint: '#8999A9',
    hairline: 'rgba(31, 62, 82, 0.09)',
    hairlineSoft: 'rgba(31, 62, 82, 0.055)',

    rust: 'var(--primary, #2E6F8E)',
    rustDeep: 'var(--secondary, #42B89B)',
    rustGlow: 'var(--primary, #2E6F8E)',

    amber: '#D78B28',
    amberGlow: '#F1B75F',

    pine: '#2E8B69',
    pineGlow: '#69B99A',

    teal: '#268AA3',
    tealGlow: '#64B8C9',

    plum: '#8D5AC8',
    plumGlow: '#B78BDF',

    indigo: '#596BCE',
    indigoGlow: '#8796E2',

    success: '#1C8A56',
    warning: '#D18A0E',
    danger: '#C9342B',
    info: '#268AA3',
};

const ACCENTS = [
    palette.rust,
    palette.teal,
    palette.amber,
    palette.pine,
    palette.plum,
    palette.indigo,
];

const COMPANY_TYPE_COLORS = {
    Operator: palette.rust,
    Vendor: palette.plum,
    Aggregator: palette.teal,
    'Solution Provider': palette.pine,
    Consultant: palette.amber,
    ISP: palette.indigo,
    Telecom: palette.rustGlow,
    Technology: '#3B4CC0',
    'Service Provider': palette.tealGlow,
    Equipment: palette.pineGlow,
    Software: palette.plumGlow,
    Network: '#4A7DE8',
    Wholesale: palette.success,
    Retail: palette.warning,
    'Cloud Provider': '#5B62D6',
    'Data Center': '#8F3A12',
    Security: palette.danger,
    Other: '#7A6B5C',
};

const fontDisplay = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontBody = "'Plus Jakarta Sans', 'Inter', sans-serif";
const fontMono = "'JetBrains Mono', 'SFMono-Regular', monospace";

const FONT_IMPORT = `@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');`;

/* ── colour utilities (never throw, unlike MUI alpha on a bad token) ───────── */

const hexToRgb = (hex) => {
    if (typeof hex !== 'string') return { r: 22, g: 18, b: 16 };
    let h = hex.replace('#', '').trim();
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length !== 6 || /[^0-9a-f]/i.test(h)) return { r: 22, g: 18, b: 16 };
    return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
    };
};

const rgba = (color, a = 1) => {
    if (typeof color === 'string' && color.startsWith('var(')) return `color-mix(in srgb, ${color} ${Math.max(0, Math.min(100, Number(a) * 100))}%, transparent)`;
    if (typeof color === 'string' && color.startsWith('rgb')) return color;
    const { r, g, b } = hexToRgb(color);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
};

/* Chart.js draws into <canvas>, so CSS tokens such as var(...) and color-mix(...)
   cannot be passed to CanvasGradient/addColorStop. Resolve theme variables to
   concrete browser colours before giving them to Chart.js. */
const resolveCanvasColor = (color, fallbackColor = '#2E6F8E') => {
    if (typeof color !== 'string' || !color.trim()) return fallbackColor;
    const value = color.trim();

    const cssVar = value.match(/^var\(\s*(--[^,\s)]+)\s*(?:,\s*([^)]+))?\)$/);
    if (cssVar) {
        const [, variableName, fallbackValue] = cssVar;
        if (typeof window !== 'undefined' && typeof document !== 'undefined') {
            const resolved = window.getComputedStyle(document.documentElement)
                .getPropertyValue(variableName)
                .trim();
            if (resolved) return resolved;
        }
        return (fallbackValue || fallbackColor).trim();
    }

    return value;
};

const canvasRgba = (color, a = 1) => {
    const resolved = resolveCanvasColor(color);
    const alpha = Math.max(0, Math.min(1, Number.isFinite(Number(a)) ? Number(a) : 1));

    if (resolved.startsWith('#')) {
        const { r, g, b } = hexToRgb(resolved);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    const rgb = resolved.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*[\d.]+)?\s*\)$/i);
    if (rgb) {
        return `rgba(${Number(rgb[1])}, ${Number(rgb[2])}, ${Number(rgb[3])}, ${alpha})`;
    }

    // All platform theme colours are stored as hex/rgb. If a legacy value is
    // malformed, fall back to the default brand colour instead of crashing a chart.
    const { r, g, b } = hexToRgb('#2E6F8E');
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/* ── the liquid-glass surface recipe ─────────────────────────────────────────
   Three stacked lights make glass read as glass:
   1. a translucent tinted body with heavy backdrop blur + saturation
   2. an inset top rim (specular highlight) and a faint bottom bounce light
   3. an outer diffuse shadow tinted with the panel's own accent
   ────────────────────────────────────────────────────────────────────────── */

const liquid = (accent = palette.rust, { depth = 1, radius = 26 } = {}) => ({
    position: 'relative',
    borderRadius: `${radius}px`,
    background: `linear-gradient(155deg, ${rgba('#FFFFFF', 0.82)} 0%, ${rgba('#FFFFFF', 0.55)} 42%, ${rgba(accent, 0.07)} 100%)`,
    backdropFilter: 'blur(22px) saturate(180%)',
    WebkitBackdropFilter: 'blur(22px) saturate(180%)',
    border: `1px solid ${rgba('#FFFFFF', 0.6)}`,
    boxShadow: [
        `inset 0 1px 0 ${rgba('#FFFFFF', 0.95)}`,
        `inset 0 -1px 0 ${rgba('#FFFFFF', 0.35)}`,
        `inset 0 0 0 1px ${rgba(accent, 0.06)}`,
        `0 ${2 * depth}px ${6 * depth}px ${rgba(palette.ink, 0.04)}`,
        `0 ${10 * depth}px ${30 * depth}px -${8 * depth}px ${rgba(accent, 0.16)}`,
    ].join(', '),
    overflow: 'hidden',
    transition: 'box-shadow .5s cubic-bezier(.22,1,.36,1), transform .5s cubic-bezier(.22,1,.36,1), border-color .4s ease',
});

/* pointer-tracked specular sheen — writes CSS vars, never triggers a re-render */
const useSheen = () => {
    const ref = useRef(null);
    const onPointerMove = useCallback((e) => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        el.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
        el.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
        el.style.setProperty('--sheen', '1');
    }, []);
    const onPointerLeave = useCallback(() => {
        const el = ref.current;
        if (el) el.style.setProperty('--sheen', '0');
    }, []);
    return { ref, onPointerMove, onPointerLeave };
};

const sheenLayer = (accent) => ({
    content: '""',
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    opacity: 'var(--sheen, 0)',
    transition: 'opacity .45s ease',
    background: `radial-gradient(420px circle at var(--mx, 50%) var(--my, 0%), ${rgba('#FFFFFF', 0.75)}, ${rgba(accent, 0.10)} 38%, transparent 68%)`,
    mixBlendMode: 'soft-light',
});

// Low-contrast decorative rings/lines add identity without competing with card content.
const cardGraphicLayer = (accent) => ({
    content: '""',
    position: 'absolute',
    width: 168,
    height: 168,
    right: -58,
    bottom: -72,
    borderRadius: '50%',
    pointerEvents: 'none',
    opacity: 0.9,
    transform: 'rotate(-14deg)',
    backgroundImage: `radial-gradient(circle at center, transparent 0 31%, ${rgba(accent, 0.11)} 32% 33%, transparent 34% 49%, ${rgba(accent, 0.075)} 50% 51%, transparent 52%), repeating-linear-gradient(135deg, transparent 0 16px, ${rgba(accent, 0.04)} 17px 18px)`,
});

/* ═══════════════════════════════════════════════════════════════════════════
   2 · FORMAT / MATH HELPERS
   ═══════════════════════════════════════════════════════════════════════════ */

const toDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value?.toDate === 'function') {
        try { return value.toDate(); } catch { return null; }
    }
    if (typeof value === 'number') return new Date(value);
    if (typeof value === 'string') {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? null : d;
    }
    return null;
};

const formatRelative = (value) => {
    const d = toDate(value);
    if (!d) return '—';
    const diff = Date.now() - d.getTime();
    if (diff < 0) return 'Scheduled';
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'Just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const days = Math.floor(h / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString();
};

const formatDateTime = (value) => {
    const d = toDate(value);
    if (!d) return 'Date TBD';
    return d.toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
    });
};

const formatClock = (ms) => {
    const total = Math.max(Math.floor(ms / 1000), 0);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const compact = (n) => {
    const v = Number(n) || 0;
    if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (Math.abs(v) >= 10_000) return `${(v / 1000).toFixed(1)}K`;
    return Math.round(v).toLocaleString();
};

const percentDelta = (current, previous) => {
    const c = Number(current) || 0;
    const p = Number(previous) || 0;
    if (p === 0) return c > 0 ? { label: 'new', trend: 'up' } : null;
    const pct = ((c - p) / p) * 100;
    if (!Number.isFinite(pct) || Math.abs(pct) < 0.5) return { label: '0%', trend: 'flat' };
    return {
        label: `${pct > 0 ? '+' : ''}${pct.toFixed(0)}%`,
        trend: pct >= 0 ? 'up' : 'down',
    };
};

/* rAF count-up that correctly re-animates when async data lands */
const useCountUp = (target, duration = 900) => {
    const [display, setDisplay] = useState(0);
    const fromRef = useRef(0);

    useEffect(() => {
        const to = Number(target) || 0;
        const from = fromRef.current;
        if (from === to) { setDisplay(to); return undefined; }

        let raf;
        const start = performance.now();
        const tick = (now) => {
            const p = Math.min((now - start) / duration, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            const current = from + (to - from) * eased;
            fromRef.current = current;
            setDisplay(current);
            if (p < 1) raf = requestAnimationFrame(tick);
            else fromRef.current = to;
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [target, duration]);

    return display;
};

/* ═══════════════════════════════════════════════════════════════════════════
   3 · LIQUID BACKDROP
   Blobs pass through an SVG turbulence/displacement filter so their edges
   wobble like a settling fluid. Memoised so parent re-renders never reseed it.
   ═══════════════════════════════════════════════════════════════════════════ */

const LiquidBackdrop = React.memo(function LiquidBackdrop() {
    const motes = useMemo(
        () =>
            Array.from({ length: 18 }, (_, i) => ({
                id: i,
                size: 3 + ((i * 7) % 9),
                x: (i * 37) % 100,
                y: (i * 53) % 100,
                duration: 26 + ((i * 5) % 18),
                delay: (i * 1.7) % 12,
                color: ACCENTS[i % ACCENTS.length],
            })),
        []
    );

    return (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}>
            <Box
                component="svg"
                sx={{ position: 'absolute', width: 0, height: 0 }}
                aria-hidden
            >
                <defs>
                    <filter id="tf-liquid">
                        <feTurbulence type="fractalNoise" baseFrequency="0.008 0.012" numOctaves="2" seed="7" result="noise">
                            <animate attributeName="baseFrequency" dur="34s" values="0.008 0.012;0.014 0.006;0.008 0.012" repeatCount="indefinite" />
                        </feTurbulence>
                        <feDisplacementMap in="SourceGraphic" in2="noise" scale="70" xChannelSelector="R" yChannelSelector="G" />
                    </filter>
                </defs>
            </Box>

            {/* molten core */}
            <motion.div
                animate={{ x: [0, 70, -30, 0], y: [0, -45, 35, 0], scale: [1, 1.14, 0.9, 1] }}
                transition={{ duration: 30, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', top: '-6%', right: '-4%',
                    width: 620, height: 620, borderRadius: '50%',
                    background: `radial-gradient(circle at 35% 35%, ${rgba(palette.rustGlow, 0.32)}, ${rgba(palette.rust, 0.14)} 45%, transparent 70%)`,
                    filter: 'url(#tf-liquid) blur(42px)',
                }}
            />
            {/* pine pool */}
            <motion.div
                animate={{ x: [0, -55, 40, 0], y: [0, 40, -35, 0], scale: [1, 0.88, 1.16, 1] }}
                transition={{ duration: 36, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', bottom: '-10%', left: '-6%',
                    width: 560, height: 560, borderRadius: '50%',
                    background: `radial-gradient(circle at 60% 40%, ${rgba(palette.pineGlow, 0.26)}, ${rgba(palette.teal, 0.12)} 48%, transparent 72%)`,
                    filter: 'url(#tf-liquid) blur(48px)',
                }}
            />
            {/* amber wash */}
            <motion.div
                animate={{ x: ['-50%', '-44%', '-56%', '-50%'], y: ['-50%', '-56%', '-45%', '-50%'], scale: [1, 1.08, 0.94, 1] }}
                transition={{ duration: 26, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                    position: 'absolute', top: '46%', left: '52%',
                    width: 760, height: 760, borderRadius: '50%',
                    background: `radial-gradient(circle, ${rgba(palette.amberGlow, 0.16)}, transparent 66%)`,
                    filter: 'blur(80px)',
                }}
            />

            {motes.map((m) => (
                <motion.span
                    key={m.id}
                    initial={{ opacity: 0 }}
                    animate={{
                        y: [`${m.y}%`, `${m.y - 45}%`, `${m.y}%`],
                        x: [`${m.x}%`, `${m.x + 14}%`, `${m.x}%`],
                        opacity: [0, 0.5, 0],
                        scale: [0.4, 1, 0.4],
                    }}
                    transition={{ duration: m.duration, delay: m.delay, repeat: Infinity, ease: 'easeInOut' }}
                    style={{
                        position: 'absolute', top: 0, left: 0,
                        width: m.size, height: m.size, borderRadius: '50%',
                        background: `radial-gradient(circle at 30% 30%, #fff, ${m.color})`,
                        boxShadow: `0 0 12px ${rgba(m.color, 0.5)}`,
                    }}
                />
            ))}

            {/* fine mesh + grain for tactile depth */}
            <Box
                sx={{
                    position: 'absolute', inset: 0, opacity: 0.55,
                    backgroundImage: `linear-gradient(${rgba(palette.ink, 0.022)} 1px, transparent 1px),
                                      linear-gradient(90deg, ${rgba(palette.ink, 0.022)} 1px, transparent 1px)`,
                    backgroundSize: '72px 72px',
                    maskImage: 'radial-gradient(circle at 50% 30%, #000 0%, transparent 78%)',
                    WebkitMaskImage: 'radial-gradient(circle at 50% 30%, #000 0%, transparent 78%)',
                }}
            />
        </Box>
    );
});

/* ═══════════════════════════════════════════════════════════════════════════
   4 · PRIMITIVES
   ═══════════════════════════════════════════════════════════════════════════ */

const Rule = ({ color = palette.rust, w = 44 }) => (
    <Box
        sx={{
            width: w, height: 3, mt: 0.75, borderRadius: 3,
            background: `linear-gradient(90deg, ${color}, ${rgba(color, 0)})`,
            animation: 'tfRule 3.4s ease-in-out infinite',
        }}
    />
);

const GlassPanel = ({
    accent = palette.rust,
    delay = 0,
    lift = true,
    padding = { xs: 2.25, md: 3 },
    children,
    sx = {},
    ...rest
}) => {
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    return (
        <motion.div
            initial={{ opacity: 0, y: 22, filter: 'blur(6px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: '100%', width: '100%', minWidth: 0 }}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquid(accent),
                    p: padding,
                    height: '100%',
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                    '&::before': cardGraphicLayer(accent),
                    '&::after': sheenLayer(accent),
                    ...(lift && {
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            borderColor: rgba('#FFFFFF', 0.85),
                            boxShadow: [
                                `inset 0 1px 0 ${rgba('#FFFFFF', 1)}`,
                                `inset 0 0 0 1px ${rgba(accent, 0.14)}`,
                                `0 6px 14px ${rgba(palette.ink, 0.06)}`,
                                `0 26px 56px -18px ${rgba(accent, 0.34)}`,
                            ].join(', '),
                        },
                    }),
                    ...sx,
                }}
                {...rest}
            >
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                    {children}
                </Box>
            </Paper>
        </motion.div>
    );
};

const GlassIcon = ({ icon, color, size = 42 }) => (
    <Box
        sx={{
            width: size, height: size, flexShrink: 0,
            borderRadius: `${size * 0.34}px`,
            display: 'grid', placeItems: 'center',
            color: '#fff',
            background: `linear-gradient(150deg, ${rgba(color, 0.95)}, ${rgba(color, 0.72)})`,
            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.55)}, inset 0 -2px 6px ${rgba(palette.ink, 0.18)}, 0 8px 20px -6px ${rgba(color, 0.55)}`,
            '& svg': { fontSize: size * 0.48, filter: 'drop-shadow(0 1px 1px rgba(0,0,0,.18))' },
        }}
    >
        {icon}
    </Box>
);

const PanelHead = ({ icon, title, accent = palette.rust, chip, action, onAction, dense = false }) => (
    <Box
        sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 1.5, flexWrap: 'wrap', mb: dense ? 1.75 : 2.5,
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            {icon && <GlassIcon icon={icon} color={accent} size={dense ? 36 : 42} />}
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    sx={{
                        fontFamily: fontDisplay, fontWeight: 600,
                        fontSize: dense ? '1.02rem' : '1.14rem',
                        color: palette.ink, letterSpacing: '-0.015em', lineHeight: 1.15,
                    }}
                >
                    {title}
                </Typography>
                <Rule color={accent} w={dense ? 34 : 44} />
            </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            {chip && (
                <Chip
                    label={chip}
                    size="small"
                    sx={{
                        height: 24, fontFamily: fontMono, fontSize: '0.62rem', fontWeight: 600,
                        letterSpacing: '0.04em', textTransform: 'uppercase',
                        color: accent, bgcolor: rgba(accent, 0.09),
                        border: `1px solid ${rgba(accent, 0.16)}`,
                        backdropFilter: 'blur(6px)',
                    }}
                />
            )}
            {action && (
                <Button
                    size="small"
                    endIcon={<ArrowForwardIcon sx={{ fontSize: 15 }} />}
                    onClick={onAction}
                    sx={{
                        color: accent, fontFamily: fontBody, fontWeight: 600,
                        textTransform: 'none', fontSize: '0.78rem', borderRadius: '10px',
                        '&:hover': { bgcolor: rgba(accent, 0.07) },
                    }}
                >
                    {action}
                </Button>
            )}
        </Box>
    </Box>
);

const EmptyState = ({ icon, text, action, onAction }) => (
    <Box sx={{ textAlign: 'center', py: 4, px: 2, flex: 1, display: 'grid', placeContent: 'center' }}>
        <Box sx={{ display: 'grid', placeItems: 'center', mb: 1 }}>
            {React.cloneElement(icon, { sx: { fontSize: 34, color: palette.inkFaint } })}
        </Box>
        <Typography sx={{ color: palette.inkLight, fontSize: '0.86rem' }}>{text}</Typography>
        {action && (
            <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={onAction}
                sx={{
                    mt: 1.75, mx: 'auto', textTransform: 'none', fontWeight: 600,
                    borderRadius: '12px', px: 2, color: '#fff',
                    background: `linear-gradient(140deg, ${palette.rust}, ${palette.rustDeep})`,
                    boxShadow: `0 8px 20px -8px ${rgba(palette.rust, 0.7)}`,
                    '&:hover': { background: `linear-gradient(140deg, ${palette.rustGlow}, ${palette.rust})` },
                }}
            >
                {action}
            </Button>
        )}
    </Box>
);

/* ── stat card ────────────────────────────────────────────────────────────── */

const StatCard = ({ label, value, suffix = '', icon, accent, delta, hint, delay = 0, loading }) => {
    const animated = useCountUp(value);
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    const trendColor =
        delta?.trend === 'up' ? palette.success : delta?.trend === 'down' ? palette.danger : palette.inkLight;

    return (
        <motion.div
            initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
            animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
            style={{ height: '100%', minWidth: 0 }}
        >
            <Paper
                ref={ref}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                elevation={0}
                sx={{
                    ...liquid(accent, { radius: 16 }),
                    height: '100%',
                    p: { xs: 1.35, md: 1.5 },
                    display: 'flex',
                    flexDirection: 'column',
                    minWidth: 0,
                    '&::before': cardGraphicLayer(accent),
                    '&::after': sheenLayer(accent),
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: [
                            `inset 0 1px 0 ${rgba('#FFFFFF', 1)}`,
                            `inset 0 0 0 1px ${rgba(accent, 0.16)}`,
                            `0 28px 60px -20px ${rgba(accent, 0.42)}`,
                        ].join(', '),
                    },
                    '&:hover .tf-orb': { opacity: 0.4, transform: 'scale(1.25)' },
                }}
            >
                <Box
                    className="tf-orb"
                    sx={{
                        position: 'absolute', top: -70, right: -70, width: 170, height: 170,
                        borderRadius: '50%', filter: 'blur(38px)', opacity: 0.16,
                        background: `radial-gradient(circle, ${accent}, transparent 68%)`,
                        transition: 'opacity .6s ease, transform .6s cubic-bezier(.22,1,.36,1)',
                        pointerEvents: 'none',
                    }}
                />

                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.75 }}>
                        <GlassIcon icon={icon} color={accent} size={38} />
                        {delta && (
                            <Chip
                                size="small"
                                icon={
                                    delta.trend === 'down'
                                        ? <TrendingDownIcon sx={{ fontSize: 13 }} />
                                        : <TrendingUpIcon sx={{ fontSize: 13 }} />
                                }
                                label={delta.label}
                                sx={{
                                    height: 22, fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 700,
                                    color: trendColor, bgcolor: rgba(trendColor, 0.09),
                                    border: `1px solid ${rgba(trendColor, 0.18)}`,
                                    '& .MuiChip-icon': { color: trendColor, ml: '6px' },
                                }}
                            />
                        )}
                    </Box>

                    {loading ? (
                        <Skeleton width="60%" height={40} sx={{ bgcolor: palette.hairline }} />
                    ) : (
                        <Typography
                            sx={{
                                fontFamily: fontDisplay, fontWeight: 600,
                                fontSize: { xs: '1.45rem', md: '1.72rem' },
                                lineHeight: 1, letterSpacing: '-0.03em',
                                background: `linear-gradient(140deg, ${palette.ink} 20%, ${accent})`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {suffix === '%' ? `${Math.round(animated)}%` : compact(animated)}
                            {suffix && suffix !== '%' && (
                                <Box component="span" sx={{ fontSize: '0.55em', ml: 0.4 }}>{suffix}</Box>
                            )}
                        </Typography>
                    )}

                    <Typography
                        sx={{
                            mt: 0.9, fontFamily: fontMono, fontSize: '0.62rem', fontWeight: 500,
                            textTransform: 'uppercase', letterSpacing: '0.12em', color: palette.inkSoft,
                        }}
                    >
                        {label}
                    </Typography>

                    <Typography
                        sx={{
                            mt: 'auto', pt: 1.5, fontSize: '0.68rem', color: palette.inkLight,
                            display: 'flex', alignItems: 'center', gap: 0.75,
                        }}
                    >
                        <Box
                            component="span"
                            sx={{
                                width: 6, height: 6, borderRadius: '50%', bgcolor: accent,
                                boxShadow: `0 0 0 3px ${rgba(accent, 0.16)}`,
                                animation: 'tfPulse 2.4s ease-in-out infinite',
                            }}
                        />
                        {hint}
                    </Typography>
                </Box>
            </Paper>
        </motion.div>
    );
};

/* ── quick action tile ────────────────────────────────────────────────────── */

const QuickAction = ({ icon, label, desc, accent, onClick, badge, delay = 0 }) => {
    const { ref, onPointerMove, onPointerLeave } = useSheen();
    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}
            whileTap={{ scale: 0.985 }}
            style={{ minWidth: 0 }}
        >
            <Box
                ref={ref}
                role="button"
                tabIndex={0}
                onClick={onClick}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
                onPointerMove={onPointerMove}
                onPointerLeave={onPointerLeave}
                sx={{
                    ...liquid(accent, { radius: 20, depth: 0.7 }),
                    cursor: 'pointer',
                    p: 1.75,
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    minHeight: 76, minWidth: 0,
                    '&::before': cardGraphicLayer(accent),
                    '&::after': sheenLayer(accent),
                    '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 1)}, 0 22px 44px -16px ${rgba(accent, 0.42)}`,
                    },
                    '&:hover .tf-arrow': { transform: 'translateX(4px)', opacity: 1 },
                    '&:focus-visible': { outline: `2px solid ${rgba(accent, 0.6)}`, outlineOffset: 2 },
                }}
            >
                <GlassIcon icon={icon} color={accent} size={38} />
                <Box sx={{ minWidth: 0, flex: 1, position: 'relative', zIndex: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: palette.ink, lineHeight: 1.25 }} noWrap>
                        {label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: palette.inkLight }} noWrap>
                        {desc}
                    </Typography>
                </Box>
                {badge > 0 && (
                    <Box
                        sx={{
                            px: 0.9, height: 20, borderRadius: '10px', display: 'grid', placeItems: 'center',
                            fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 700, color: '#fff',
                            background: `linear-gradient(140deg, ${palette.danger}, #96201A)`,
                            boxShadow: `0 4px 12px -3px ${rgba(palette.danger, 0.7)}`,
                            position: 'relative', zIndex: 1,
                        }}
                    >
                        {badge > 99 ? '99+' : badge}
                    </Box>
                )}
                <ArrowForwardIcon
                    className="tf-arrow"
                    sx={{
                        fontSize: 17, color: accent, opacity: 0.35, position: 'relative', zIndex: 1,
                        transition: 'transform .3s cubic-bezier(.22,1,.36,1), opacity .3s ease',
                    }}
                />
            </Box>
        </motion.div>
    );
};

/* ── list rows ────────────────────────────────────────────────────────────── */

const STATUS_TONE = {
    completed: palette.success,
    confirmed: palette.pine,
    active: palette.pine,
    ongoing: palette.teal,
    pending: palette.warning,
    cancelled: palette.danger,
    rejected: palette.danger,
};

const toneFor = (status) => STATUS_TONE[String(status || '').toLowerCase()] || palette.inkLight;

const LiquidRow = ({ accent, onClick, children, index = 0 }) => (
    <motion.div
        initial={{ opacity: 0, x: -14 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.35, delay: Math.min(index, 8) * 0.045, ease: [0.22, 1, 0.36, 1] }}
    >
        <Box
            onClick={onClick}
            sx={{
                p: 1.35, mb: 1, borderRadius: '16px',
                display: 'flex', alignItems: 'center', gap: 1.35, minWidth: 0,
                cursor: onClick ? 'pointer' : 'default',
                background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.7)}, ${rgba('#FFFFFF', 0.34)})`,
                border: `1px solid ${rgba('#FFFFFF', 0.7)}`,
                boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.8)}`,
                transition: 'transform .35s cubic-bezier(.22,1,.36,1), box-shadow .35s ease, background .35s ease',
                '&:hover': {
                    transform: 'translateX(5px)',
                    background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.9)}, ${rgba(accent, 0.08)})`,
                    boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 1)}, 0 12px 26px -14px ${rgba(accent, 0.6)}`,
                },
            }}
        >
            {children}
        </Box>
    </motion.div>
);

const StatusPill = ({ status }) => {
    const tone = toneFor(status);
    return (
        <Box
            className="tf-admin-dashboard-page"
            sx={{
                px: 0.9, py: 0.28, borderRadius: '8px', flexShrink: 0,
                fontFamily: fontMono, fontSize: '0.56rem', fontWeight: 600,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                color: tone, bgcolor: rgba(tone, 0.1), border: `1px solid ${rgba(tone, 0.2)}`,
            }}
        >
            {status || 'unknown'}
        </Box>
    );
};

/* ═══════════════════════════════════════════════════════════════════════════
   5 · SESSION GUARD
   Self-contained so its 1-second tick never re-renders the whole dashboard.
   Activity extends the session only *before* the warning window opens — once
   the warning is up, the user must explicitly extend (or be logged out).
   ═══════════════════════════════════════════════════════════════════════════ */

const SESSION = {
    timeout: 30 * 60 * 1000,
    warningAt: 5 * 60 * 1000,
};

const SessionGuard = ({ onExpire }) => {
    const expiryRef = useRef(Date.now() + SESSION.timeout);
    const expiredRef = useRef(false);
    const [remaining, setRemaining] = useState(SESSION.timeout);
    const [warning, setWarning] = useState(false);

    const extend = useCallback(() => {
        expiryRef.current = Date.now() + SESSION.timeout;
        expiredRef.current = false;
        setRemaining(SESSION.timeout);
        setWarning(false);
        toast.success('Session extended', { icon: '🔄', duration: 1800 });
    }, []);

    useEffect(() => {
        const bump = () => {
            const left = expiryRef.current - Date.now();
            if (left <= SESSION.warningAt) return; // inside warning window → needs explicit extend
            expiryRef.current = Date.now() + SESSION.timeout;
        };
        const events = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
        events.forEach((e) => window.addEventListener(e, bump, { passive: true }));

        const id = setInterval(() => {
            const left = Math.max(expiryRef.current - Date.now(), 0);
            setRemaining(left);
            if (left <= 0) {
                if (!expiredRef.current) {
                    expiredRef.current = true;
                    onExpire();
                }
                return;
            }
            setWarning(left <= SESSION.warningAt);
        }, 1000);

        return () => {
            events.forEach((e) => window.removeEventListener(e, bump));
            clearInterval(id);
        };
    }, [onExpire]);

    const critical = remaining <= SESSION.warningAt;
    const tone = critical ? palette.warning : palette.pine;

    return (
        <>
            <Tooltip title="Time left before automatic logout" arrow>
                <Box
                    sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.75,
                        px: 1.25, height: 30, borderRadius: '999px',
                        border: `1px solid ${rgba(tone, 0.22)}`,
                        background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.75)}, ${rgba(tone, 0.09)})`,
                        backdropFilter: 'blur(10px)',
                        boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.9)}`,
                    }}
                >
                    <TimerIcon sx={{ fontSize: 13, color: tone }} />
                    <Typography sx={{ fontFamily: fontMono, fontSize: '0.68rem', fontWeight: 600, color: tone, fontVariantNumeric: 'tabular-nums' }}>
                        {formatClock(remaining)}
                    </Typography>
                </Box>
            </Tooltip>

            <Dialog
                open={warning}
                onClose={() => {}}
                maxWidth="xs"
                fullWidth
                slotProps={{ paper: {
                    sx: {
                        ...liquid(palette.warning, { radius: 26 }),
                        backgroundImage: 'none',
                        background: `linear-gradient(155deg, ${rgba('#FFFFFF', 0.94)}, ${rgba(palette.warning, 0.1)})`,
                    },
                } }}
            >
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Box sx={{ display: 'grid', placeItems: 'center', mb: 2 }}>
                        <GlassIcon icon={<WarningIcon />} color={palette.warning} size={56} />
                    </Box>
                    <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.25rem', color: palette.ink }}>
                        Session expiring
                    </Typography>
                    <Typography sx={{ color: palette.inkSoft, fontSize: '0.86rem', mt: 0.75, mb: 2 }}>
                        You&apos;ll be signed out automatically for security. Extend to keep working.
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: fontMono, fontSize: '2.4rem', fontWeight: 700,
                            color: palette.warning, fontVariantNumeric: 'tabular-nums', lineHeight: 1,
                        }}
                    >
                        {formatClock(remaining)}
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={(remaining / SESSION.warningAt) * 100}
                        sx={{
                            mt: 2, mb: 2.5, height: 5, borderRadius: 5,
                            bgcolor: rgba(palette.warning, 0.12),
                            '& .MuiLinearProgress-bar': { borderRadius: 5, bgcolor: palette.warning },
                        }}
                    />
                    <Box sx={{ display: 'flex', gap: 1.25 }}>
                        <Button
                            fullWidth
                            onClick={onExpire}
                            sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600, color: palette.inkSoft }}
                        >
                            Log out now
                        </Button>
                        <Button
                            fullWidth
                            variant="contained"
                            disableElevation
                            startIcon={<TimerIcon />}
                            onClick={extend}
                            sx={{
                                borderRadius: '12px', textTransform: 'none', fontWeight: 700,
                                background: `linear-gradient(140deg, ${palette.teal}, #0A4C55)`,
                                boxShadow: `0 10px 24px -10px ${rgba(palette.teal, 0.9)}`,
                            }}
                        >
                            Extend
                        </Button>
                    </Box>
                </Box>
            </Dialog>
        </>
    );
};

/* ═══════════════════════════════════════════════════════════════════════════
   6 · FLOATING SUPPORT BUTTON  (module scope → mounts once, no listener churn)
   ═══════════════════════════════════════════════════════════════════════════ */

const FloatingSupportButton = React.memo(function FloatingSupportButton({ onOpen }) {
    const [unread, setUnread] = useState(0);

    useEffect(() => {
        let cancelled = false;

        const unsubscribe = onSnapshot(
            query(collection(db, 'supportTickets'), where('status', 'in', ['open', 'in_progress']), limit(50)),
            async (snap) => {
                // Fast path: a denormalised counter on the ticket document.
                let total = 0;
                let needsFallback = false;
                snap.forEach((d) => {
                    const data = d.data() || {};
                    const counter = data.adminUnreadCount ?? data.unreadForAdmin;
                    if (typeof counter === 'number') total += counter;
                    else needsFallback = true;
                });

                if (!needsFallback) {
                    if (!cancelled) setUnread(total);
                    return;
                }

                // Fallback: aggregate counts, capped so we never fan out unbounded.
                try {
                    const docs = snap.docs.slice(0, 15);
                    const counts = await Promise.all(
                        docs.map(async (d) => {
                            const q = query(
                                collection(db, 'supportTickets', d.id, 'messages'),
                                where('read', '==', false),
                                where('senderType', '==', 'company')
                            );
                            const agg = await getCountFromServer(q);
                            return agg.data().count;
                        })
                    );
                    if (!cancelled) setUnread(counts.reduce((a, b) => a + b, 0));
                } catch (err) {
                    console.warn('[support] unread count unavailable:', err.message);
                }
            },
            (err) => console.warn('[support] listener unavailable:', err.message)
        );

        return () => { cancelled = true; unsubscribe(); };
    }, []);

    return (
        <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.6, type: 'spring', stiffness: 240, damping: 20 }}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1200 }}
        >
            <Badge
                badgeContent={unread}
                max={99}
                sx={{
                    '& .MuiBadge-badge': {
                        fontFamily: fontMono, fontSize: '0.62rem', fontWeight: 700,
                        height: 22, minWidth: 22, borderRadius: '11px',
                        color: '#fff', background: `linear-gradient(140deg, ${palette.danger}, #94211B)`,
                        border: `2px solid ${rgba('#FFFFFF', 0.9)}`,
                        boxShadow: `0 6px 16px -4px ${rgba(palette.danger, 0.8)}`,
                    },
                }}
            >
                <Fab
                    variant="extended"
                    onClick={onOpen}
                    sx={{
                        px: 2.5, height: 52, borderRadius: '18px', textTransform: 'none',
                        color: '#fff', fontFamily: fontBody, fontWeight: 600, fontSize: '0.85rem',
                        background: `linear-gradient(140deg, ${palette.plum}, #3B0E4C)`,
                        border: `1px solid ${rgba('#FFFFFF', 0.28)}`,
                        boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.4)}, 0 14px 34px -12px ${rgba(palette.plum, 0.9)}`,
                        '&:hover': {
                            background: `linear-gradient(140deg, ${palette.plumGlow}, ${palette.plum})`,
                            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.5)}, 0 18px 44px -12px ${rgba(palette.plum, 1)}`,
                        },
                    }}
                >
                    <SupportAgentIcon sx={{ mr: 1.25, fontSize: 20 }} />
                    Support
                </Fab>
            </Badge>
        </motion.div>
    );
});

/* ═══════════════════════════════════════════════════════════════════════════
   7 · NOTIFICATION COMPOSER
   Isolated state — typing here no longer re-renders charts or listeners.
   ═══════════════════════════════════════════════════════════════════════════ */

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

const glassField = (accent) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '14px',
        fontSize: '0.875rem',
        background: rgba('#FFFFFF', 0.6),
        backdropFilter: 'blur(8px)',
        transition: 'box-shadow .3s ease',
        '& fieldset': { borderColor: palette.hairline, borderWidth: '1.5px' },
        '&:hover fieldset': { borderColor: rgba(accent, 0.4) },
        '&.Mui-focused fieldset': { borderColor: accent, borderWidth: '1.5px' },
        '&.Mui-focused': { boxShadow: `0 0 0 4px ${rgba(accent, 0.12)}` },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: accent },
});

const FieldGroup = ({ dot, title, hint, children }) => (
    <Box>
        <Typography
            sx={{
                display: 'flex', alignItems: 'center', gap: 0.9, mb: 1.25,
                fontFamily: fontMono, fontSize: '0.62rem', fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: '0.14em', color: palette.inkLight,
            }}
        >
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: dot, boxShadow: `0 0 0 3px ${rgba(dot, 0.18)}` }} />
            {title}
            {hint && (
                <Box component="span" sx={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400, color: palette.inkFaint }}>
                    {hint}
                </Box>
            )}
        </Typography>
        {children}
    </Box>
);

const NotificationComposer = ({ open, onClose, companies, author }) => {
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [type, setType] = useState('info');
    const [priority, setPriority] = useState('normal');
    const [link, setLink] = useState('');
    const [audience, setAudience] = useState('all');
    const [company, setCompany] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [progress, setProgress] = useState(0);
    const [sending, setSending] = useState(false);

    const reset = useCallback(() => {
        setTitle(''); setMessage(''); setType('info'); setPriority('normal');
        setLink(''); setAudience('all'); setCompany(null);
        setImageFile(null); setImagePreview(''); setProgress(0);
    }, []);

    useEffect(() => { if (open) reset(); }, [open, reset]);

    const handleClose = () => { if (!sending) onClose(); };

    const handlePick = (e) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // allow re-selecting the same file
        if (!file) return;
        if (!file.type.startsWith('image/')) { toast.error('Please choose an image file'); return; }
        if (file.size > MAX_IMAGE_BYTES) { toast.error('Image must be under 5 MB'); return; }
        setImageFile(file);
        const reader = new FileReader();
        reader.onloadend = () => setImagePreview(String(reader.result || ''));
        reader.readAsDataURL(file);
    };

    const uploadImage = async (file) => {
        const asset = await uploadAdminFile({ file, scope: 'platform', folder: 'notifications', kind: 'image', maxBytes: MAX_IMAGE_BYTES, onProgress: setProgress });
        return asset.url;
    };

    const validLink = !link.trim() || /^https?:\/\/.+/i.test(link.trim());
    const ready =
        title.trim().length > 0 &&
        message.trim().length > 0 &&
        validLink &&
        (audience === 'all' || Boolean(company));

    const handleSend = async () => {
        if (!ready || sending) {
            if (audience === 'company' && !company) toast.error('Select a company first');
            else if (!validLink) toast.error('Action link must start with http:// or https://');
            else toast.error('Add a title and a message');
            return;
        }

        setSending(true);
        setProgress(0);
        try {
            let imageUrl = '';
            if (imageFile) imageUrl = await uploadImage(imageFile);

            const targetId = company?.companyId || company?.id || null;
            const targetName = company?.companyName || company?.name || null;

            const response = await sendPlatformNotification({
                title: title.trim(),
                message: message.trim(),
                type,
                priority,
                imageUrl: imageUrl || '',
                link: link.trim() || '',
                audience,
                companyId: audience === 'company' ? targetId : '',
                companyName: audience === 'company' ? targetName : '',
                sentBy: author?.name || 'Admin',
                sendEmail: true,
                sendPush: true,
            });
            const delivery = response?.delivery || {};
            const detail = [
                `${delivery.inApp || 0} in-app`,
                delivery.email ? `${delivery.email} email` : null,
                delivery.push ? `${delivery.push} push` : null,
            ].filter(Boolean).join(' · ');
            toast.success(`${audience === 'all' ? 'Broadcast sent' : `Notification sent to ${targetName}`}${detail ? ` — ${detail}` : ''}`);
            if (delivery.emailError) toast.error(`Email delivery: ${delivery.emailError}`);
            if (delivery.pushError) toast.error(`Push delivery: ${delivery.pushError}`);

            reset();
            onClose();
        } catch (err) {
            console.error('[notifications] send failed:', err);
            toast.error(`Failed to send: ${err.message}`);
        } finally {
            setSending(false);
            setProgress(0);
        }
    };

    const typeTone =
        type === 'error' ? palette.danger :
        type === 'warning' ? palette.warning :
        type === 'success' ? palette.success : palette.teal;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            TransitionComponent={Fade}
            slotProps={{ paper: {
                sx: {
                    ...liquid(palette.teal, { radius: 28 }),
                    background: `linear-gradient(160deg, ${rgba('#FFFFFF', 0.95)}, ${rgba(palette.canvas, 0.92)} 60%, ${rgba(palette.teal, 0.1)})`,
                    maxHeight: '90vh',
                },
            } }}
        >
            <Box
                sx={{
                    p: 2.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    borderBottom: `1px solid ${palette.hairlineSoft}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                    <GlassIcon icon={<NotificationIcon />} color={palette.teal} size={46} />
                    <Box>
                        <Typography sx={{ fontFamily: fontDisplay, fontWeight: 600, fontSize: '1.2rem', color: palette.ink, letterSpacing: '-0.02em' }}>
                            Compose Notification
                        </Typography>
                        <Typography sx={{ color: palette.inkLight, fontSize: '0.78rem' }}>
                            Broadcast to everyone, or target a single company
                        </Typography>
                    </Box>
                </Box>
                <IconButton
                    onClick={handleClose}
                    disabled={sending}
                    sx={{
                        width: 36, height: 36, borderRadius: '12px', color: palette.inkSoft,
                        bgcolor: rgba(palette.ink, 0.04),
                        transition: 'transform .3s ease, background .3s ease',
                        '&:hover': { bgcolor: rgba(palette.ink, 0.08), transform: 'rotate(90deg)' },
                    }}
                >
                    <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>

            <DialogContent sx={{ px: 2.75, py: 2.5, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <FieldGroup dot={palette.teal} title="Audience">
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                        {[
                            { value: 'all', label: 'All users', icon: <GroupIcon sx={{ fontSize: 17 }}  key="config-1394-0-2" />, tone: palette.teal },
                            { value: 'company', label: 'Specific company', icon: <BusinessIcon sx={{ fontSize: 17 }}  key="config-1395-1-2" />, tone: palette.amber },
                        ].map((opt) => {
                            const selected = audience === opt.value;
                            return (
                                <Box
                                    key={opt.value}
                                    onClick={() => setAudience(opt.value)}
                                    sx={{
                                        p: 1.5, borderRadius: '16px', cursor: 'pointer',
                                        display: 'flex', alignItems: 'center', gap: 1.25,
                                        border: `1.5px solid ${selected ? rgba(opt.tone, 0.6) : palette.hairline}`,
                                        background: selected
                                            ? `linear-gradient(140deg, ${rgba('#FFFFFF', 0.9)}, ${rgba(opt.tone, 0.12)})`
                                            : rgba('#FFFFFF', 0.5),
                                        boxShadow: selected ? `0 10px 24px -14px ${rgba(opt.tone, 0.9)}` : 'none',
                                        transition: 'all .3s cubic-bezier(.22,1,.36,1)',
                                        '&:hover': { borderColor: rgba(opt.tone, 0.45), transform: 'translateY(-2px)' },
                                    }}
                                >
                                    <GlassIcon icon={opt.icon} color={selected ? opt.tone : palette.inkFaint} size={32} />
                                    <Typography sx={{ fontWeight: 600, fontSize: '0.84rem', color: selected ? opt.tone : palette.inkSoft }}>
                                        {opt.label}
                                    </Typography>
                                    {selected && (
                                        <Box sx={{ ml: 'auto', width: 20, height: 20, borderRadius: '50%', bgcolor: opt.tone, display: 'grid', placeItems: 'center' }}>
                                            <CheckIcon sx={{ fontSize: 13, color: '#fff' }} />
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>

                    <AnimatePresence initial={false}>
                        {audience === 'company' && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.28, ease: 'easeOut' }}
                                style={{ overflow: 'hidden' }}
                            >
                                <Box sx={{ pt: 1.5 }}>
                                    <Autocomplete
                                        options={companies}
                                        value={company}
                                        onChange={(_, v) => setCompany(v)}
                                        isOptionEqualToValue={(o, v) => o.id === v?.id}
                                        getOptionLabel={(o) => o.companyName || o.name || 'Unnamed company'}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                size="small"
                                                label="Select company"
                                                placeholder="Search by name…"
                                                error={audience === 'company' && !company}
                                                helperText={audience === 'company' && !company ? 'Required for targeted delivery' : ' '}
                                                slotProps={{ input: {
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <>
                                                            <SearchIcon sx={{ fontSize: 18, color: palette.inkFaint, mr: 0.5 }} />
                                                            {params.InputProps.startAdornment}
                                                        </>
                                                    ),
                                                } }}
                                                sx={glassField(palette.amber)}
                                            />
                                        )}
                                    />
                                </Box>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </FieldGroup>

                <FieldGroup dot={palette.amber} title="Content">
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, gap: 1.5 }}>
                        <TextField
                            fullWidth size="small" label="Title" placeholder="Notification headline"
                            value={title} onChange={(e) => setTitle(e.target.value)}
                            inputProps={{ maxLength: 80 }}
                            sx={glassField(palette.teal)}
                        />
                        <FormControl fullWidth size="small" sx={glassField(palette.teal)}>
                            <InputLabel>Priority</InputLabel>
                            <Select value={priority} label="Priority" onChange={(e) => setPriority(e.target.value)}>
                                {[
                                    { v: 'low', c: palette.inkFaint }, { v: 'normal', c: palette.teal },
                                    { v: 'high', c: palette.warning }, { v: 'urgent', c: palette.danger },
                                ].map((p) => (
                                    <MenuItem key={p.v} value={p.v} sx={{ textTransform: 'capitalize' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.c }} />
                                            {p.v}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth size="small" sx={glassField(palette.teal)}>
                            <InputLabel>Type</InputLabel>
                            <Select value={type} label="Type" onChange={(e) => setType(e.target.value)}>
                                <MenuItem value="info">Info</MenuItem>
                                <MenuItem value="success">Success</MenuItem>
                                <MenuItem value="warning">Warning</MenuItem>
                                <MenuItem value="error">Error</MenuItem>
                                <MenuItem value="announcement">Announcement</MenuItem>
                                <MenuItem value="promotion">Promotion</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    <TextField
                        fullWidth multiline rows={3} size="small" label="Message"
                        placeholder="What do you want them to know?"
                        value={message} onChange={(e) => setMessage(e.target.value)}
                        inputProps={{ maxLength: 500 }}
                        helperText={
                            <Box component="span" sx={{ display: 'block', textAlign: 'right', fontFamily: fontMono, fontSize: '0.65rem' }}>
                                {message.length}/500
                            </Box>
                        }
                        sx={{ mt: 1.5, ...glassField(palette.teal) }}
                    />
                </FieldGroup>

                <FieldGroup dot={palette.inkFaint} title="Attachments" hint="(optional)">
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
                        <Box
                            sx={{
                                p: 1.5, borderRadius: '16px', minHeight: 80,
                                display: 'flex', flexDirection: 'column', justifyContent: 'center',
                                border: `1.5px dashed ${imagePreview ? rgba(palette.teal, 0.5) : palette.hairline}`,
                                background: imagePreview ? rgba(palette.teal, 0.05) : rgba('#FFFFFF', 0.5),
                                transition: 'all .3s ease',
                            }}
                        >
                            {imagePreview ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box
                                        component="img" src={imagePreview} alt="Attachment preview"
                                        sx={{ width: 52, height: 52, borderRadius: '12px', objectFit: 'cover', border: `1px solid ${palette.hairline}` }}
                                    />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: palette.ink }} noWrap>
                                            {imageFile?.name || 'Image attached'}
                                        </Typography>
                                        <Typography sx={{ fontSize: '0.68rem', color: palette.inkLight, fontFamily: fontMono }}>
                                            {imageFile ? `${(imageFile.size / 1024).toFixed(0)} KB` : ''}
                                        </Typography>
                                    </Box>
                                    <IconButton
                                        size="small"
                                        onClick={() => { setImageFile(null); setImagePreview(''); }}
                                        sx={{ color: palette.danger, bgcolor: rgba(palette.danger, 0.08), borderRadius: '10px' }}
                                    >
                                        <DeleteIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </Box>
                            ) : (
                                <Box sx={{ textAlign: 'center' }}>
                                    <Button
                                        component="label"
                                        size="small"
                                        startIcon={<ImageOutlinedIcon />}
                                        sx={{ textTransform: 'none', color: palette.inkSoft, fontWeight: 600, '&:hover': { color: palette.teal, bgcolor: 'transparent' } }}
                                    >
                                        Add image
                                        <input type="file" accept="image/*" hidden onChange={handlePick} />
                                    </Button>
                                    <Typography sx={{ fontSize: '0.65rem', color: palette.inkFaint, fontFamily: fontMono }}>
                                        PNG · JPG · GIF — max 5 MB
                                    </Typography>
                                </Box>
                            )}
                            {sending && imageFile && (
                                <LinearProgress
                                    variant="determinate" value={progress}
                                    sx={{ mt: 1.25, height: 4, borderRadius: 4, bgcolor: rgba(palette.teal, 0.12), '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: palette.teal } }}
                                />
                            )}
                        </Box>

                        <TextField
                            fullWidth size="small" label="Action link" placeholder="https://example.com/page"
                            value={link} onChange={(e) => setLink(e.target.value)}
                            error={!validLink}
                            helperText={!validLink ? 'Must start with http:// or https://' : ' '}
                            slotProps={{ input: { startAdornment: <LinkIcon sx={{ fontSize: 17, color: palette.inkFaint, mr: 0.75 }} /> } }}
                            sx={glassField(palette.teal)}
                        />
                    </Box>
                </FieldGroup>

                {(title || message) && (
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                        <Box sx={{ p: 1.75, borderRadius: '18px', background: rgba(palette.ink, 0.035), border: `1px solid ${palette.hairlineSoft}` }}>
                            <Typography sx={{ fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: palette.inkLight, mb: 1.25 }}>
                                Live preview
                            </Typography>
                            <Box
                                sx={{
                                    ...liquid(typeTone, { radius: 18, depth: 0.6 }),
                                    p: 1.6, display: 'flex', gap: 1.4, alignItems: 'flex-start',
                                }}
                            >
                                {imagePreview ? (
                                    <Box component="img" src={imagePreview} alt="" sx={{ width: 44, height: 44, borderRadius: '12px', objectFit: 'cover', flexShrink: 0 }} />
                                ) : (
                                    <GlassIcon icon={<NotificationIcon />} color={typeTone} size={44} />
                                )}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                        <Typography sx={{ fontWeight: 700, fontSize: '0.84rem', color: palette.ink }} noWrap>
                                            {title || 'Notification title'}
                                        </Typography>
                                        <Typography sx={{ fontFamily: fontMono, fontSize: '0.6rem', color: palette.inkFaint, flexShrink: 0 }}>
                                            now
                                        </Typography>
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: '0.78rem', color: palette.inkSoft, lineHeight: 1.45,
                                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                        }}
                                    >
                                        {message || 'Your message will appear here…'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
                                        <StatusPill status={type} />
                                        <StatusPill status={priority} />
                                        {audience === 'company' && company && <StatusPill status={company.companyName || company.name} />}
                                        {link && validLink && <StatusPill status="has link" />}
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </motion.div>
                )}
            </DialogContent>

            <Box
                sx={{
                    px: 2.75, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    borderTop: `1px solid ${palette.hairlineSoft}`, background: rgba('#FFFFFF', 0.45), backdropFilter: 'blur(8px)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                        sx={{
                            width: 8, height: 8, borderRadius: '50%',
                            bgcolor: ready ? palette.success : palette.inkFaint,
                            boxShadow: ready ? `0 0 0 4px ${rgba(palette.success, 0.18)}` : 'none',
                            transition: 'all .3s ease',
                        }}
                    />
                    <Typography sx={{ fontSize: '0.75rem', color: palette.inkLight }}>
                        {ready ? 'Ready to send' : 'Complete the required fields'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.25 }}>
                    <Button onClick={handleClose} disabled={sending} sx={{ textTransform: 'none', fontWeight: 600, color: palette.inkSoft, borderRadius: '12px', px: 2 }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={handleSend}
                        disabled={!ready || sending}
                        startIcon={sending ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SendIcon sx={{ fontSize: 17 }} />}
                        sx={{
                            textTransform: 'none', fontWeight: 700, borderRadius: '14px', px: 2.75, py: 1,
                            color: '#fff',
                            background: `linear-gradient(140deg, ${palette.teal}, #0A4C55)`,
                            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.28)}, 0 12px 28px -12px ${rgba(palette.teal, 0.95)}`,
                            '&:hover': { background: `linear-gradient(140deg, ${palette.tealGlow}, ${palette.teal})` },
                            '&.Mui-disabled': { background: rgba(palette.ink, 0.08), color: palette.inkFaint, boxShadow: 'none' },
                        }}
                    >
                        {sending ? 'Sending…' : 'Send notification'}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};

/* ═══════════════════════════════════════════════════════════════════════════
   8 · DASHBOARD
   ═══════════════════════════════════════════════════════════════════════════ */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const IGNORED_DOC_IDS = new Set(['_schema', '_placeholder', '_init']);

/* last 6 calendar months as {key:'2026-03', label:'Mar'} — year-aware so a
   meeting from last March can never land in this March's bucket. */
const lastSixMonths = () => {
    const out = [];
    const now = new Date();
    for (let i = 5; i >= 0; i -= 1) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        out.push({ key: `${d.getFullYear()}-${d.getMonth()}`, label: MONTHS[d.getMonth()] });
    }
    return out;
};

const Dashboard = () => {
    const navigate = useNavigate();
    const { currentUser, userData, logout } = useAuth();

    // react-chartjs-2 normally destroys instances on unmount. Explicit refs/cleanup
    // make the dashboard resilient to React StrictMode and hot reloads as well.
    const trendChartRef = useRef(null);
    const volumeChartRef = useRef(null);
    const statusChartRef = useRef(null);
    const typeChartRef = useRef(null);

    useEffect(() => () => {
        [trendChartRef, volumeChartRef, statusChartRef, typeChartRef].forEach((chartRef) => {
            const chart = chartRef.current;
            if (chart && typeof chart.destroy === 'function') {
                try { chart.destroy(); } catch (error) {
                    if (process.env.NODE_ENV === 'development') {
                        console.debug('Chart cleanup skipped:', error?.message || error);
                    }
                }
            }
            chartRef.current = null;
        });
    }, []);

    const isAdmin = useMemo(
        () => ['admin', 'super_admin'].includes(String(userData?.role || '').toLowerCase()),
        [userData?.role]
    );

    /* ── company-name cache (kills the N+1 lookup on every snapshot) ───────── */
    const nameCache = useRef(new Map());
    const missedIds = useRef(new Set());

    const resolveCompanyNames = useCallback(async (ids = []) => {
        const pending = [...new Set(ids)].filter(
            (id) => id && !nameCache.current.has(id) && !missedIds.current.has(id)
        );
        if (!pending.length) return;

        const absorb = (snap) => {
            snap.forEach((d) => {
                const data = d.data() || {};
                const name = data.companyName || data.name || 'Unnamed company';
                nameCache.current.set(d.id, name);
                if (data.companyId) nameCache.current.set(data.companyId, name);
            });
        };

        for (let i = 0; i < pending.length; i += 10) {
            const chunk = pending.slice(i, i + 10);
            try {
                absorb(await getDocs(query(collection(db, 'companies'), where(documentId(), 'in', chunk))));
                const stillMissing = chunk.filter((id) => !nameCache.current.has(id));
                if (stillMissing.length) {
                    absorb(await getDocs(query(collection(db, 'companies'), where('companyId', 'in', stillMissing))));
                }
                // remember genuine misses so we never re-query them
                chunk.forEach((id) => { if (!nameCache.current.has(id)) missedIds.current.add(id); });
            } catch (err) {
                console.warn('[companies] name resolution failed:', err.message);
            }
        }
    }, []);

    const nameOf = useCallback((id) => nameCache.current.get(id) || 'Unknown company', []);

    /* ── state ─────────────────────────────────────────────────────────────── */
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [composerOpen, setComposerOpen] = useState(false);

    const [counts, setCounts] = useState({
        users: 0, companies: 0,
        pending: 0, active: 0, completed: 0, cancelled: 0,
        newUsers30: null, newCompanies30: null,
        incompleteRegistrations: 0, pendingExtraSeatPayments: 0, manualPayments: 0,
    });
    const [live, setLive] = useState({
        activeCalls: 0, participants: 0, unreadNotifications: 0,
        revenue: 0, activeSponsorships: 0,
    });

    const [trends, setTrends] = useState([]);
    const [companyTypes, setCompanyTypes] = useState({ labels: [], values: [], colors: [] });
    const [topCompanies, setTopCompanies] = useState([]);
    const [allCompanies, setAllCompanies] = useState([]);
    const [activities, setActivities] = useState([]);
    const [upcoming, setUpcoming] = useState([]);
    const [sponsorships, setSponsorships] = useState([]);

    /* ── one-shot aggregate load ───────────────────────────────────────────── */
    const fetchDashboardData = useCallback(async (silent = false) => {
        if (silent) setRefreshing(true); else setLoading(true);
        const monthWindow = lastSixMonths();
        const since30 = Timestamp.fromDate(new Date(Date.now() - 30 * 86400000));

        try {
            const meetingCount = (status) =>
                getCountFromServer(query(collection(db, 'meetings'), where('status', '==', status)));

            const results = await Promise.allSettled([
                getCountFromServer(collection(db, 'users')),
                getCountFromServer(collection(db, 'companies')),
                meetingCount('pending'),
                getCountFromServer(query(collection(db, 'meetings'), where('status', 'in', ['ongoing', 'confirmed', 'active']))),
                meetingCount('completed'),
                meetingCount('cancelled'),
                getCountFromServer(query(collection(db, 'users'), where('createdAt', '>=', since30))),
                getCountFromServer(query(collection(db, 'companies'), where('createdAt', '>=', since30))),
                getCountFromServer(query(collection(db, 'users'), where('registrationIncomplete', '==', true))),
                getCountFromServer(query(collection(db, 'users'), where('paymentStatus', '==', 'extra_seat_payment_pending'))),
                getCountFromServer(query(collection(db, 'paymentProofs'), where('source', '==', 'manual'))),
            ]);

            const value = (i) => (results[i].status === 'fulfilled' ? results[i].value.data().count : 0);
            const optional = (i) => (results[i].status === 'fulfilled' ? results[i].value.data().count : null);

            setCounts({
                users: value(0),
                companies: value(1),
                pending: value(2),
                active: value(3),
                completed: value(4),
                cancelled: value(5),
                newUsers30: optional(6),
                newCompanies30: optional(7),
                incompleteRegistrations: optional(8) || 0,
                pendingExtraSeatPayments: optional(9) || 0,
                manualPayments: optional(10) || 0,
            });

            /* companies — directory, type mix, and cache warm-up */
            const compSnap = await getDocs(query(collection(db, 'companies'), limit(200)));
            const list = [];
            const typeTally = {};
            compSnap.forEach((d) => {
                if (IGNORED_DOC_IDS.has(d.id)) return;
                const data = d.data() || {};
                const companyName = data.companyName || data.name || 'Unnamed company';
                list.push({ id: d.id, ...data, companyName });
                nameCache.current.set(d.id, companyName);
                if (data.companyId) nameCache.current.set(data.companyId, companyName);
                const t = data.companyType || 'Other';
                typeTally[t] = (typeTally[t] || 0) + 1;
            });
            setAllCompanies(list);

            const sortedTypes = Object.entries(typeTally).sort((a, b) => b[1] - a[1]).slice(0, 10);
            setCompanyTypes({
                labels: sortedTypes.map(([t]) => t),
                values: sortedTypes.map(([, n]) => n),
                colors: sortedTypes.map(([t], i) => COMPANY_TYPE_COLORS[t] || ACCENTS[i % ACCENTS.length]),
            });

            /* meetings sample → month trends + genuine "top companies by volume" */
            const buckets = new Map(monthWindow.map((m) => [m.key, { ...m, scheduled: 0, completed: 0, cancelled: 0, total: 0 }]));
            const volume = new Map();

            try {
                const sample = await getDocs(query(collection(db, 'meetings'), orderBy('createdAt', 'desc'), limit(400)));
                sample.forEach((d) => {
                    if (IGNORED_DOC_IDS.has(d.id)) return;
                    const data = d.data() || {};
                    const created = toDate(data.createdAt);
                    if (created) {
                        const key = `${created.getFullYear()}-${created.getMonth()}`;
                        const bucket = buckets.get(key);
                        if (bucket) {
                            const status = String(data.status || 'pending').toLowerCase();
                            if (status === 'completed') bucket.completed += 1;
                            else if (status === 'cancelled' || status === 'rejected') bucket.cancelled += 1;
                            else bucket.scheduled += 1;
                            bucket.total += 1;
                        }
                    }
                    [data.requesterId, data.targetId].forEach((id) => {
                        if (id) volume.set(id, (volume.get(id) || 0) + 1);
                    });
                });
            } catch (err) {
                console.warn('[meetings] trend sample unavailable:', err.message);
            }

            setTrends([...buckets.values()]);

            const ranked = [...volume.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5);
            await resolveCompanyNames(ranked.map(([id]) => id));
            setTopCompanies(
                ranked.map(([id, meetings]) => {
                    const match = list.find((c) => c.id === id || c.companyId === id);
                    return {
                        id: match?.id || id,
                        name: match?.companyName || nameOf(id),
                        type: match?.companyType || '—',
                        meetings,
                    };
                })
            );
        } catch (err) {
            console.error('[dashboard] load failed:', err);
            toast.error('Could not load dashboard data');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [resolveCompanyNames, nameOf]);

    useEffect(() => { fetchDashboardData(); }, [fetchDashboardData]);

    /* ── realtime listeners ────────────────────────────────────────────────── */
    useEffect(() => {
        const subs = [];

        // recent meeting activity
        subs.push(onSnapshot(
            query(collection(db, 'meetings'), orderBy('updatedAt', 'desc'), limit(10)),
            async (snap) => {
                const docs = snap.docs.filter((d) => !IGNORED_DOC_IDS.has(d.id)).map((d) => ({ id: d.id, ...d.data() }));
                await resolveCompanyNames(docs.flatMap((m) => [m.requesterId, m.targetId]));
                setActivities(docs.map((m) => ({
                    id: m.id,
                    status: m.status || 'pending',
                    title: `${nameOf(m.requesterId)} → ${nameOf(m.targetId)}`,
                    description: m.serviceCategory || 'Meeting',
                    time: formatRelative(m.updatedAt || m.createdAt),
                })));
            },
            (err) => console.warn('[meetings] activity listener:', err.message)
        ));

        // upcoming meetings (single-field range + sort → no composite index needed)
        subs.push(onSnapshot(
            query(
                collection(db, 'meetings'),
                where('scheduledTime', '>=', Timestamp.fromDate(new Date())),
                orderBy('scheduledTime', 'asc'),
                limit(6)
            ),
            async (snap) => {
                const docs = snap.docs.filter((d) => !IGNORED_DOC_IDS.has(d.id)).map((d) => ({ id: d.id, ...d.data() }));
                await resolveCompanyNames(docs.flatMap((m) => [m.requesterId, m.targetId]));
                setUpcoming(docs.map((m) => ({
                    ...m,
                    label: `${nameOf(m.requesterId)} → ${nameOf(m.targetId)}`,
                })));
            },
            (err) => console.warn('[meetings] upcoming listener:', err.message)
        ));

        // live rooms
        subs.push(onSnapshot(
            query(collection(db, 'virtualRooms'), where('status', '==', 'active')),
            (snap) => {
                let participants = 0;
                snap.forEach((d) => { participants += Number(d.data()?.currentParticipants) || 0; });
                setLive((prev) => ({ ...prev, activeCalls: snap.size, participants }));
            },
            (err) => console.warn('[rooms] listener:', err.message)
        ));

        // sponsorships → recent three + real revenue + active count
        subs.push(onSnapshot(
            query(collection(db, 'sponsorships'), orderBy('createdAt', 'desc'), limit(200)),
            async (snap) => {
                const docs = snap.docs.filter((d) => !IGNORED_DOC_IDS.has(d.id)).map((d) => ({ id: d.id, ...d.data() }));
                await resolveCompanyNames(docs.slice(0, 3).map((s) => s.companyId));

                const revenue = docs.reduce((sum, s) => {
                    const paid = String(s.paymentStatus || '').toLowerCase() === 'paid';
                    return paid ? sum + (Number(s.amount) || 0) : sum;
                }, 0);
                const activeSponsorships = docs.filter((s) => s.isActive).length;

                setLive((prev) => ({ ...prev, revenue, activeSponsorships }));
                setSponsorships(docs.slice(0, 3).map((s) => ({
                    ...s,
                    companyName: s.companyName || nameOf(s.companyId),
                })));
            },
            (err) => console.warn('[sponsorships] listener:', err.message)
        ));

        // unread notifications
        subs.push(onSnapshot(
            query(collection(db, 'notifications'), where('read', '==', false), limit(200)),
            (snap) => setLive((prev) => ({ ...prev, unreadNotifications: snap.size })),
            (err) => console.warn('[notifications] listener:', err.message)
        ));

        return () => subs.forEach((unsub) => unsub());
    }, [resolveCompanyNames, nameOf]);

    /* ── logout / session ──────────────────────────────────────────────────── */
    const handleExpire = useCallback(async () => {
        toast.error('Session expired — signing you out', { icon: '⏰', duration: 4000 });
        try { await logout(); } catch (err) { console.error('[auth] logout failed:', err); }
        navigate('/login', { replace: true });
    }, [logout, navigate]);

    /* ── derived numbers ───────────────────────────────────────────────────── */
    const totalMeetings = counts.pending + counts.active + counts.completed + counts.cancelled;
    const pipelineTotal = Math.max(totalMeetings, 1);

    const decided = counts.completed + counts.cancelled;
    const completionRate = decided > 0 ? Math.round((counts.completed / decided) * 100) : 0;

    const meetingDelta = useMemo(() => {
        if (trends.length < 2) return null;
        return percentDelta(trends[trends.length - 1].total, trends[trends.length - 2].total);
    }, [trends]);

    const statCards = useMemo(() => [
        {
            label: 'Total users', value: counts.users, icon: <PeopleIcon  key="config-2015-0-2" />, accent: palette.rust,
            delta: counts.newUsers30 ? { label: `+${compact(counts.newUsers30)}`, trend: 'up' } : null,
            hint: counts.newUsers30 != null ? `${compact(counts.newUsers30)} joined in 30d` : 'Directory total',
        },
        {
            label: 'Companies', value: counts.companies, icon: <BusinessIcon  key="config-2020-1-2" />, accent: palette.pine,
            delta: counts.newCompanies30 ? { label: `+${compact(counts.newCompanies30)}`, trend: 'up' } : null,
            hint: `${companyTypes.labels.length || 0} distinct types`,
        },
        {
            label: 'Meetings', value: totalMeetings, icon: <EventIcon  key="config-2025-2-2" />, accent: palette.teal,
            delta: meetingDelta, hint: `${compact(counts.pending)} awaiting action`,
        },
        {
            label: 'Completion rate', value: completionRate, suffix: '%', icon: <TaskAltIcon  key="config-2029-3-3" />, accent: palette.amber,
            hint: decided > 0 ? `${compact(counts.completed)} of ${compact(decided)} resolved` : 'No resolved meetings yet',
        },
        {
            label: 'Live calls', value: live.activeCalls, icon: <VideocamIcon  key="config-2033-4-2" />, accent: palette.plum,
            hint: `${compact(live.participants)} participants online`,
        },
        {
            label: 'Revenue (USD)', value: live.revenue, icon: <PaidIcon  key="config-2037-5-2" />, accent: palette.indigo,
            hint: `${compact(live.activeSponsorships)} active packages`,
        },
        {
            label: 'Incomplete registration', value: counts.incompleteRegistrations, icon: <PersonAddIcon  key="config-2041-6-2" />, accent: palette.plum,
            hint: `${compact(counts.pendingExtraSeatPayments)} waiting on extra-seat payment`,
        },
        {
            label: 'Payment verification', value: counts.manualPayments, icon: <ReceiptLongIcon  key="config-2045-7-2" />, accent: palette.pine,
            hint: 'Offline payments recorded by Super Admin',
        },
    ], [counts, totalMeetings, completionRate, decided, meetingDelta, live, companyTypes.labels.length]);

    const quickActions = useMemo(() => [
        { icon: <EventIcon  key="config-2052-0-0" />, label: 'Schedule meeting', desc: 'Book a new session', accent: palette.rust, onClick: () => navigate('/meetings') },
        { icon: <PeopleIcon  key="config-2053-1-0" />, label: 'Add user', desc: 'Invite a teammate', accent: palette.pine, onClick: () => navigate('/users') },
        { icon: <BusinessIcon  key="config-2054-2-0" />, label: 'Add company', desc: 'Register an exhibitor', accent: palette.amber, onClick: () => navigate('/companies') },
        { icon: <NotificationIcon  key="config-2055-3-0" />, label: 'Send notification', desc: 'Broadcast a message', accent: palette.teal, badge: live.unreadNotifications, onClick: () => setComposerOpen(true) },
        { icon: <PersonAddIcon  key="config-2056-4-0" />, label: 'Incomplete registrations', desc: 'Resume or remind users', accent: palette.plum, badge: counts.incompleteRegistrations, onClick: () => navigate('/registrations/incomplete') },
        { icon: <ReceiptLongIcon  key="config-2057-5-0" />, label: 'Verify payments', desc: 'Review UPI & bank transfers', accent: palette.pine, onClick: () => navigate('/payments/manual') },
    ], [navigate, live.unreadNotifications, counts.incompleteRegistrations]);

    /* ── chart configuration ───────────────────────────────────────────────── */

    const chartPrimary = resolveCanvasColor(palette.rust);

    const areaFill = (color) => (ctx) => {
        const { chart } = ctx;
        const { ctx: c, chartArea } = chart;
        if (!chartArea) return canvasRgba(color, 0.12);
        const g = c.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
        g.addColorStop(0, canvasRgba(color, 0.34));
        g.addColorStop(1, canvasRgba(color, 0));
        return g;
    };

    const baseOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true, pointStyle: 'circle', padding: 16, boxWidth: 8,
                    color: palette.inkSoft,
                    font: { family: fontBody, size: 11, weight: 600 },
                },
            },
            tooltip: {
                backgroundColor: rgba(palette.ink, 0.92),
                titleFont: { family: fontBody, size: 12, weight: 700 },
                bodyFont: { family: fontMono, size: 11 },
                padding: 12, cornerRadius: 12, displayColors: true, boxPadding: 4,
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                border: { display: false },
                grid: { color: rgba(palette.ink, 0.05) },
                ticks: { precision: 0, color: palette.inkLight, font: { family: fontMono, size: 10 } },
            },
            x: {
                border: { display: false },
                grid: { display: false },
                ticks: { color: palette.inkLight, font: { family: fontMono, size: 10 } },
            },
        },
    }), []);

    const donutOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        cutout: '66%',
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true, pointStyle: 'circle', padding: 12, boxWidth: 8,
                    color: palette.inkSoft,
                    font: { family: fontBody, size: 11, weight: 600 },
                },
            },
            tooltip: {
                backgroundColor: rgba(palette.ink, 0.92),
                bodyFont: { family: fontBody, size: 11 },
                padding: 12, cornerRadius: 12,
            },
        },
    }), []);

    const trendChart = useMemo(() => ({
        labels: trends.map((t) => t.label),
        datasets: [
            {
                label: 'Scheduled', data: trends.map((t) => t.scheduled),
                borderColor: chartPrimary, backgroundColor: areaFill(chartPrimary),
                fill: true, tension: 0.42, borderWidth: 2.5, pointRadius: 3.5,
                pointBackgroundColor: chartPrimary, pointBorderColor: '#fff', pointBorderWidth: 2,
                pointHoverRadius: 6,
            },
            {
                label: 'Completed', data: trends.map((t) => t.completed),
                borderColor: palette.pine, backgroundColor: areaFill(palette.pine),
                fill: true, tension: 0.42, borderWidth: 2.5, pointRadius: 3.5,
                pointBackgroundColor: palette.pine, pointBorderColor: '#fff', pointBorderWidth: 2,
                pointHoverRadius: 6,
            },
            {
                label: 'Cancelled', data: trends.map((t) => t.cancelled),
                borderColor: palette.danger, backgroundColor: areaFill(palette.danger),
                fill: true, tension: 0.42, borderWidth: 2, pointRadius: 3,
                pointBackgroundColor: palette.danger, pointBorderColor: '#fff', pointBorderWidth: 2,
                pointHoverRadius: 6,
            },
        ],
    }), [trends, chartPrimary]);

    const volumeChart = useMemo(() => ({
        labels: trends.map((t) => t.label),
        datasets: [{
            label: 'Meetings created',
            data: trends.map((t) => t.total),
            backgroundColor: (ctx) => {
                const { chart } = ctx;
                const { ctx: c, chartArea } = chart;
                if (!chartArea) return rgba(palette.teal, 0.6);
                const g = c.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
                g.addColorStop(0, rgba(palette.teal, 0.25));
                g.addColorStop(1, rgba(palette.tealGlow, 0.9));
                return g;
            },
            borderColor: rgba(palette.teal, 0.9),
            borderWidth: 1.5,
            borderRadius: 10,
            maxBarThickness: 44,
        }],
    }), [trends]);

    const statusChart = useMemo(() => ({
        labels: ['Pending', 'Active', 'Completed', 'Cancelled'],
        datasets: [{
            data: [counts.pending, counts.active, counts.completed, counts.cancelled],
            backgroundColor: [palette.warning, palette.teal, palette.success, palette.danger],
            borderColor: rgba('#FFFFFF', 0.9),
            borderWidth: 3,
            hoverOffset: 10,
        }],
    }), [counts]);

    const typeChart = useMemo(() => ({
        labels: companyTypes.labels,
        datasets: [{
            data: companyTypes.values,
            backgroundColor: companyTypes.colors.map((color) => resolveCanvasColor(color)),
            borderColor: rgba('#FFFFFF', 0.9),
            borderWidth: 3,
            hoverOffset: 10,
        }],
    }), [companyTypes]);

    const hasTrendData = trends.some((t) => t.total > 0);
    const pipeline = [
        { key: 'pending', label: 'Pending', value: counts.pending, tone: palette.warning },
        { key: 'active', label: 'Active / confirmed', value: counts.active, tone: palette.teal },
        { key: 'completed', label: 'Completed', value: counts.completed, tone: palette.success },
        { key: 'cancelled', label: 'Cancelled', value: counts.cancelled, tone: palette.danger },
    ];

    const chartShell = { position: 'relative', flex: 0, height: { xs: 150, sm: 160, md: 165 }, minHeight: { xs: 150, sm: 160, md: 165 }, width: '100%' };

    /* ═══════════════════════════════════════════════════════════════════════
       RENDER
       ═══════════════════════════════════════════════════════════════════════ */

    return (
        <Box
            sx={{
                minHeight: '100vh',
                position: 'relative',
                fontFamily: fontBody,
                color: palette.ink,
                background: `linear-gradient(180deg, ${palette.canvas} 0%, ${palette.canvasDeep} 100%)`,
                '&::before': {
                    content: '""', position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', opacity: 0.4,
                    backgroundImage:
                        'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27140%27 height=%27140%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.85%27 numOctaves=%272%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.03%27/%3E%3C/svg%3E")',
                    animation: 'tfGrain 8s steps(6) infinite',
                },
            }}
        >
            <style>{FONT_IMPORT}</style>
            <LiquidBackdrop />
            <Box sx={{ position: 'relative', zIndex: 2 }}></Box>

            <Container
                maxWidth={false}
                disableGutters
                sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 3, lg: 5 }, py: { xs: 3, md: 4 } }}
            >
                {/* ── Company Portal parity hero ───────────────────────────── */}
                <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
                >
                    <Box
                        sx={{
                            position: 'relative', overflow: 'hidden', borderRadius: '20px', mb: 1.35,
                            p: { xs: 2, sm: 2.25, md: 2.5 }, color: '#fff',
                            background: 'linear-gradient(112deg, #246E8B 0%, #2E8096 48%, #35AE92 100%)',
                            border: '1px solid rgba(255,255,255,.24)',
                            boxShadow: '0 14px 34px rgba(25,57,78,.12), inset 0 1px 0 rgba(255,255,255,.20)',
                            '&::before': {
                                content: '""', position: 'absolute', width: 330, height: 330, borderRadius: '50%',
                                right: -70, top: -170, background: 'rgba(255,255,255,.075)', pointerEvents: 'none',
                            },
                            '&::after': {
                                content: '""', position: 'absolute', width: 210, height: 210, borderRadius: '50%',
                                left: '42%', bottom: -165, background: 'rgba(255,255,255,.065)', pointerEvents: 'none',
                            },
                        }}
                    >
                        <Box sx={{ position: 'relative', zIndex: 1, display: 'grid', gap: { xs: 1.35, md: 1.5 }, gridTemplateColumns: { xs: '1fr', lg: 'minmax(0,1.55fr) minmax(330px,.85fr)' }, alignItems: 'center' }}>
                            <Box sx={{ minWidth: 0 }}>
                                <Chip
                                    icon={<RocketIcon sx={{ color: '#fff!important', fontSize: 13 }} />}
                                    label="PLATFORM COMMAND CENTER"
                                    size="small"
                                    sx={{ height: 25, mb: 1.05, color: '#fff', bgcolor: 'rgba(255,255,255,.11)', border: '1px solid rgba(255,255,255,.20)', fontWeight: 850, fontSize: '.58rem', letterSpacing: '.04em' }}
                                />
                                <Typography sx={{ fontFamily: fontDisplay, fontWeight: 850, fontSize: { xs: '1.85rem', sm: '2.15rem', md: '2.45rem' }, lineHeight: 1.03, letterSpacing: '-.045em', color: '#fff' }}>
                                    Welcome back, {userData?.displayName || currentUser?.displayName || 'Administrator'}
                                </Typography>
                                <Typography sx={{ mt: .65, maxWidth: 760, color: 'rgba(255,255,255,.83)', fontSize: { xs: '.76rem', md: '.82rem' }, lineHeight: 1.55 }}>
                                    One operational workspace connecting companies, users, employees, CRM, registrations, payments and platform intelligence.
                                </Typography>
                                <Box sx={{ display: 'flex', gap: .65, flexWrap: 'wrap', mt: 1.15 }}>
                                    <Chip size="small" label={`${compact(counts.companies)} companies`} sx={{ height: 22, bgcolor: 'rgba(255,255,255,.10)', color: '#fff', border: '1px solid rgba(255,255,255,.18)', fontSize: '.56rem', fontWeight: 750 }} />
                                    <Chip size="small" label={`${compact(counts.users)} users`} sx={{ height: 22, bgcolor: 'rgba(255,255,255,.10)', color: '#fff', border: '1px solid rgba(255,255,255,.18)', fontSize: '.56rem', fontWeight: 750 }} />
                                    <Chip size="small" label={`${compact(totalMeetings)} meetings`} sx={{ height: 22, bgcolor: 'rgba(255,255,255,.10)', color: '#fff', border: '1px solid rgba(255,255,255,.18)', fontSize: '.56rem', fontWeight: 750 }} />
                                </Box>
                                <Box sx={{ display: 'flex', gap: .75, flexWrap: 'wrap', mt: 1.15 }}>
                                    <Button onClick={() => navigate('/companies')} startIcon={<BusinessIcon />} sx={{ minHeight: 34, px: 1.35, color: '#fff', bgcolor: 'rgba(255,255,255,.13)', border: '1px solid rgba(255,255,255,.24)', '&:hover': { bgcolor: 'rgba(255,255,255,.20)' } }}>Companies</Button>
                                    <Button onClick={() => navigate('/registrations/incomplete')} startIcon={<PersonAddIcon />} sx={{ minHeight: 34, px: 1.35, color: '#fff', bgcolor: 'rgba(255,255,255,.13)', border: '1px solid rgba(255,255,255,.24)', '&:hover': { bgcolor: 'rgba(255,255,255,.20)' } }}>Registrations</Button>
                                </Box>
                            </Box>

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr' }, gap: .8, minWidth: 0 }}>
                                {[
                                    { label: 'TOTAL USERS', value: counts.users, Icon: PeopleIcon, color: '#4E8DF5' },
                                    { label: 'COMPANIES', value: counts.companies, Icon: BusinessIcon, color: '#F1B044' },
                                    { label: 'INCOMPLETE REG.', value: counts.incompleteRegistrations, Icon: PersonAddIcon, color: '#13A773' },
                                    { label: 'MEETINGS', value: totalMeetings, Icon: EventIcon, color: '#C157B8' },
                                ].map((item) => (
                                    <Box key={item.label} sx={{ minHeight: 66, borderRadius: '12px', p: .9, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: .7, background: 'rgba(255,255,255,.095)', border: '1px solid rgba(255,255,255,.22)', backdropFilter: 'blur(8px)' }}>
                                        <Box sx={{ width: 29, height: 29, borderRadius: '9px', display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,.13)', '& svg': { fontSize: 15, color: '#fff' } }}>{React.createElement(item.Icon)}</Box>
                                        <Box sx={{ minWidth: 0, textAlign: 'right' }}>
                                            <Typography sx={{ fontSize: { xs: '1.15rem', md: '1.3rem' }, lineHeight: 1, fontWeight: 900, color: '#fff' }}>{compact(item.value)}</Typography>
                                            <Typography sx={{ mt: .35, fontSize: '.49rem', letterSpacing: '.06em', fontWeight: 800, color: 'rgba(255,255,255,.72)', whiteSpace: 'nowrap' }}>{item.label}</Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </motion.div>

                {/* ── Connected command centers ─────────────────────────────── */}
                <Paper elevation={0} sx={{ mb: 1.35, p: { xs: 1.2, md: 1.35 }, borderRadius: '18px', border: '1px solid rgba(31,62,82,.08)', background: 'rgba(255,255,255,.82)', boxShadow: '0 7px 20px rgba(25,57,78,.07)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: .7 }}>
                            <Box sx={{ width: 31, height: 31, borderRadius: '10px', display: 'grid', placeItems: 'center', color: '#fff', background: 'linear-gradient(145deg,#13A7A2,#2BB89B)', boxShadow: '0 7px 16px rgba(19,167,162,.22)' }}><RocketIcon sx={{ fontSize: 16 }} /></Box>
                            <Box><Typography sx={{ fontSize: '.79rem', fontWeight: 850, color: '#182235' }}>Connected Command Centers</Typography><Typography sx={{ fontSize: '.57rem', color: '#7D8CA0' }}>Open the platform tools you use most without leaving the workspace.</Typography></Box>
                        </Box>
                        <Chip size="small" label="3 live workspaces" sx={{ height: 21, bgcolor: 'rgba(19,167,115,.08)', color: '#14845A', border: '1px solid rgba(19,167,115,.11)', fontSize: '.52rem', fontWeight: 800 }} />
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3,minmax(0,1fr))' }, gap: .9 }}>
                        {[
                            { title: 'Workforce & CRM', eyebrow: 'OPERATIONS', desc: 'Manage employees, dynamic roles, CRM leads, deals and internal employee meetings.', accent: '#2F80ED', soft: '#E9F2FF', Icon: PeopleIcon, tags: ['Employees','CRM','Meetings'], path: '/employees' },
                            { title: 'Company Network', eyebrow: 'PLATFORM DIRECTORY', desc: 'Manage companies, users, account verification and incomplete registration activity.', accent: '#9B51E0', soft: '#F4EBFF', Icon: BusinessIcon, tags: ['Companies','Users','Registrations'], path: '/companies' },
                            { title: 'Commercial & Growth', eyebrow: 'REVENUE WORKSPACE', desc: 'Control subscription plans, manual payments, advertisements and sponsorship operations.', accent: '#13A773', soft: '#E8F8F2', Icon: PaidIcon, tags: ['Plans','Payments','Ads'], path: '/subscription-plans' },
                        ].map((item) => (
                            <Box key={item.title} onClick={() => navigate(item.path)} sx={{ cursor: 'pointer', position: 'relative', overflow: 'hidden', minHeight: 170, p: 1.25, borderRadius: '16px', border: `1px solid ${rgba(item.accent,.16)}`, background: `linear-gradient(135deg,#fff 0%,${item.soft} 100%)`, boxShadow: '0 7px 18px rgba(25,57,78,.055)', transition: '.18s ease', '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 12px 25px ${rgba(item.accent,.13)}` }, '&::after': { content: '""', position: 'absolute', width: 150, height: 150, borderRadius: '50%', right: -48, bottom: -65, background: rgba(item.accent,.09) } }}>
                                <Box sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                        <Box sx={{ width: 38, height: 38, borderRadius: '11px', display: 'grid', placeItems: 'center', color: '#fff', background: `linear-gradient(145deg,${item.accent},${rgba(item.accent,.72)})`, boxShadow: `0 8px 18px ${rgba(item.accent,.24)}`, '& svg': { fontSize: 19 } }}>{React.createElement(item.Icon)}</Box>
                                        <Chip label={item.eyebrow} size="small" sx={{ height: 20, bgcolor: rgba(item.accent,.07), color: item.accent, border: `1px solid ${rgba(item.accent,.12)}`, fontSize: '.48rem', fontWeight: 850 }} />
                                    </Box>
                                    <Typography sx={{ mt: 1, color: '#182235', fontWeight: 850, fontSize: '.9rem' }}>{item.title}</Typography>
                                    <Typography sx={{ mt: .35, color: '#617287', fontSize: '.62rem', lineHeight: 1.5 }}>{item.desc}</Typography>
                                    <Box sx={{ display: 'flex', gap: .45, flexWrap: 'wrap', mt: .75 }}>{item.tags.map((tag) => <Chip key={tag} label={tag} size="small" sx={{ height: 19, fontSize: '.48rem', fontWeight: 750, color: item.accent, bgcolor: rgba(item.accent,.06), border: `1px solid ${rgba(item.accent,.10)}` }} />)}</Box>
                                    <Box sx={{ mt: 'auto', pt: .8, alignSelf: 'flex-end' }}><Button size="small" endIcon={<ArrowForwardIcon />} sx={{ minHeight: 29, px: 1.05, color: '#fff', background: item.accent, '&:hover': { background: item.accent, filter: 'brightness(.95)' } }}>Open Dashboard</Button></Box>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Paper>

                {/* ── Platform snapshot ─────────────────────────────────────── */}
                <Paper elevation={0} sx={{ mb: 1.55, p: { xs: 1.15, md: 1.3 }, borderRadius: '18px', border: '1px solid rgba(31,62,82,.08)', background: 'rgba(255,255,255,.82)', boxShadow: '0 7px 20px rgba(25,57,78,.07)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: .9 }}>
                        <Box><Typography sx={{ fontSize: '.78rem', fontWeight: 850, color: '#182235' }}>Platform Snapshot</Typography><Typography sx={{ fontSize: '.56rem', color: '#7D8CA0' }}>Live platform activity with visual context</Typography></Box>
                        <Chip size="small" icon={<LiveIcon sx={{ fontSize: '8px!important', color: '#13A773!important' }} />} label="Live data" sx={{ height: 20, bgcolor: 'rgba(19,167,115,.07)', color: '#14845A', border: '1px solid rgba(19,167,115,.10)', fontSize: '.49rem', fontWeight: 800 }} />
                    </Box>
                    <Box sx={{ display: 'grid', gap: .8, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', xl: 'repeat(4,minmax(0,1fr))' } }}>
                        {statCards.slice(0,4).map((s, i) => <StatCard key={s.label} {...s} delay={i * .04} loading={loading} />)}
                    </Box>
                </Paper>

                {/* ── trends + operations rail ──────────────────────────── */}
                <Box
                    sx={{
                        display: 'grid', gap: 2.5, mb: 2.5,
                        gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.65fr) minmax(0, 1fr)' },
                        alignItems: 'stretch',
                    }}
                >
                    {/* Meeting Trends and Pipeline deliberately share one column and
                        equal grid tracks so Pipeline sits directly below Trends. */}
                    <Box sx={{ display: 'grid', gap: 2.5, gridTemplateRows: { xs: 'auto auto', lg: '1fr 1fr' }, minWidth: 0 }}>
                        <GlassPanel accent={palette.rust} delay={0.05}>
                            <PanelHead
                                icon={<AnalyticsIcon />}
                                title="Meeting trends"
                                accent={palette.rust}
                                chip="last 6 months"
                            />
                            <Box sx={chartShell}>
                                {loading ? (
                                    <Skeleton variant="rounded" height="100%" sx={{ bgcolor: palette.hairline, borderRadius: '18px' }} />
                                ) : hasTrendData ? (
                                    <Line ref={trendChartRef} data={trendChart} options={baseOptions} />
                                ) : (
                                    <EmptyState icon={<AnalyticsIcon />} text="No meetings created in this window yet" />
                                )}
                            </Box>
                        </GlassPanel>

                        <GlassPanel accent={palette.amber} delay={0.1}>
                            <PanelHead
                                icon={<ScheduleIcon />}
                                title="Pipeline"
                                accent={palette.amber}
                                chip={`${compact(totalMeetings)} total`}
                                dense
                            />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.35, flex: 1, justifyContent: 'center', minHeight: chartShell.height }}>
                                {pipeline.map((stage, i) => {
                                    const pct = (stage.value / pipelineTotal) * 100;
                                    return (
                                        <Box key={stage.key}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography sx={{ fontSize: '0.76rem', fontWeight: 600, color: palette.inkSoft }}>
                                                    {stage.label}
                                                </Typography>
                                                <Typography sx={{ fontFamily: fontMono, fontSize: '0.74rem', fontWeight: 700, color: stage.tone }}>
                                                    {compact(stage.value)}
                                                    <Box component="span" sx={{ color: palette.inkFaint, ml: 0.75, fontWeight: 400 }}>
                                                        {pct.toFixed(0)}%
                                                    </Box>
                                                </Typography>
                                            </Box>
                                            <Box
                                                sx={{
                                                    height: 7, borderRadius: 999, overflow: 'hidden',
                                                    background: rgba(palette.ink, 0.06),
                                                    boxShadow: `inset 0 1px 2px ${rgba(palette.ink, 0.1)}`,
                                                }}
                                            >
                                                <motion.div
                                                    initial={{ width: 0 }}
                                                    animate={{ width: `${Math.min(pct, 100)}%` }}
                                                    transition={{ duration: 0.9, delay: 0.15 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                                                    style={{
                                                        height: '100%',
                                                        borderRadius: 999,
                                                        background: `linear-gradient(90deg, ${rgba(stage.tone, 0.75)}, ${stage.tone})`,
                                                        boxShadow: `0 0 12px ${rgba(stage.tone, 0.55)}`,
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </GlassPanel>
                    </Box>

                    <GlassPanel accent={palette.teal} delay={0.12} lift={false}>
                        <PanelHead icon={<RocketIcon />} title="Quick actions" accent={palette.teal} dense />
                        <Box sx={{ display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr' }, alignContent: 'start' }}>
                            {quickActions.map((a, i) => (
                                <QuickAction key={a.label} {...a} delay={0.14 + i * 0.05} />
                            ))}
                        </Box>
                    </GlassPanel>
                </Box>

                {/* ── distribution row ─────────────────────────────────────── */}
                <Box
                    sx={{
                        display: 'grid', gap: 2.5, mb: 2.5,
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', xl: 'minmax(0, 1.3fr) minmax(0, 1fr) minmax(0, 1fr)' },
                    }}
                >
                    <GlassPanel accent={palette.teal} delay={0.05}>
                        <PanelHead icon={<TrendingUpIcon />} title="Monthly volume" accent={palette.teal} chip="created" dense />
                        <Box sx={chartShell}>
                            {loading ? (
                                <Skeleton variant="rounded" height="100%" sx={{ bgcolor: palette.hairline, borderRadius: '18px' }} />
                            ) : hasTrendData ? (
                                <Bar ref={volumeChartRef} data={volumeChart} options={baseOptions} />
                            ) : (
                                <EmptyState icon={<TrendingUpIcon />} text="No volume to plot yet" />
                            )}
                        </Box>
                    </GlassPanel>

                    <GlassPanel accent={palette.pine} delay={0.1}>
                        <PanelHead icon={<EventIcon />} title="Meeting status" accent={palette.pine} chip="split" dense />
                        <Box sx={chartShell}>
                            {loading ? (
                                <Skeleton variant="circular" width="70%" height="70%" sx={{ bgcolor: palette.hairline, mx: 'auto' }} />
                            ) : totalMeetings > 0 ? (
                                <Doughnut ref={statusChartRef} data={statusChart} options={donutOptions} />
                            ) : (
                                <EmptyState icon={<EventIcon />} text="No meetings recorded" />
                            )}
                        </Box>
                    </GlassPanel>

                    <GlassPanel accent={palette.plum} delay={0.15}>
                        <PanelHead
                            icon={<BusinessIcon />}
                            title="Company mix"
                            accent={palette.plum}
                            chip={`${companyTypes.labels.length} types`}
                            dense
                        />
                        <Box sx={chartShell}>
                            {loading ? (
                                <Skeleton variant="circular" width="70%" height="70%" sx={{ bgcolor: palette.hairline, mx: 'auto' }} />
                            ) : companyTypes.labels.length > 0 ? (
                                <Doughnut ref={typeChartRef} data={typeChart} options={donutOptions} />
                            ) : (
                                <EmptyState icon={<BusinessIcon />} text="No companies registered" />
                            )}
                        </Box>
                    </GlassPanel>
                </Box>

                {/* ── feeds row ────────────────────────────────────────────── */}
                <Box
                    sx={{
                        display: 'grid', gap: 2.5, mb: 2.5,
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', xl: 'repeat(3, minmax(0, 1fr))' },
                    }}
                >
                    {/* recent activity */}
                    <GlassPanel accent={palette.rust} delay={0.05}>
                        <PanelHead
                            icon={<AnalyticsIcon />}
                            title="Recent activity"
                            accent={palette.rust}
                            action="All"
                            onAction={() => navigate('/meetings')}
                            dense
                        />
                        <Box sx={{ flex: 1, maxHeight: 330, overflowY: 'auto', pr: 0.5, mr: -0.5 }}>
                            {loading ? (
                                Array.from({ length: 4 }).map((_, i) => (
                                    <Skeleton key={i} variant="rounded" height={58} sx={{ mb: 1, borderRadius: '16px', bgcolor: palette.hairline }} />
                                ))
                            ) : activities.length === 0 ? (
                                <EmptyState icon={<AnalyticsIcon />} text="Nothing has moved recently" />
                            ) : (
                                activities.slice(0, 6).map((a, i) => (
                                    <LiquidRow key={a.id} accent={toneFor(a.status)} index={i} onClick={() => navigate(`/meetings/${a.id}`)}>
                                        <GlassIcon icon={<EventIcon />} color={toneFor(a.status)} size={34} />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                                                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: palette.ink, minWidth: 0 }} noWrap>
                                                    {a.title}
                                                </Typography>
                                                <StatusPill status={a.status} />
                                            </Box>
                                            <Typography sx={{ fontSize: '0.68rem', color: palette.inkLight }} noWrap>
                                                {a.time} · {a.description}
                                            </Typography>
                                        </Box>
                                    </LiquidRow>
                                ))
                            )}
                        </Box>
                    </GlassPanel>

                    {/* upcoming meetings */}
                    <GlassPanel accent={palette.plum} delay={0.1}>
                        <PanelHead
                            icon={<CalendarIcon />}
                            title="Upcoming"
                            accent={palette.plum}
                            chip={`${upcoming.length}`}
                            action="All"
                            onAction={() => navigate('/meetings')}
                            dense
                        />
                        <Box sx={{ flex: 1, maxHeight: 330, overflowY: 'auto', pr: 0.5, mr: -0.5 }}>
                            {loading ? (
                                Array.from({ length: 4 }).map((_, i) => (
                                    <Skeleton key={i} variant="rounded" height={58} sx={{ mb: 1, borderRadius: '16px', bgcolor: palette.hairline }} />
                                ))
                            ) : upcoming.length === 0 ? (
                                <EmptyState
                                    icon={<CalendarIcon />}
                                    text="Nothing scheduled ahead"
                                    action="Schedule one"
                                    onAction={() => navigate('/meetings')}
                                />
                            ) : (
                                upcoming.slice(0, 5).map((m, i) => (
                                    <LiquidRow key={m.id} accent={toneFor(m.status)} index={i} onClick={() => navigate(`/meetings/${m.id}`)}>
                                        <Box
                                            sx={{
                                                width: 42, flexShrink: 0, textAlign: 'center', py: 0.5, borderRadius: '12px',
                                                background: rgba(toneFor(m.status), 0.1),
                                                border: `1px solid ${rgba(toneFor(m.status), 0.18)}`,
                                            }}
                                        >
                                            <Typography sx={{ fontFamily: fontMono, fontSize: '0.55rem', color: palette.inkLight, textTransform: 'uppercase' }}>
                                                {toDate(m.scheduledTime) ? MONTHS[toDate(m.scheduledTime).getMonth()] : '—'}
                                            </Typography>
                                            <Typography sx={{ fontFamily: fontDisplay, fontSize: '1rem', fontWeight: 700, color: toneFor(m.status), lineHeight: 1 }}>
                                                {toDate(m.scheduledTime) ? toDate(m.scheduledTime).getDate() : '–'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: palette.ink }} noWrap>
                                                {m.label}
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.68rem', color: palette.inkLight }} noWrap>
                                                {formatDateTime(m.scheduledTime)}
                                                {m.serviceCategory ? ` · ${m.serviceCategory}` : ''}
                                            </Typography>
                                        </Box>
                                        <StatusPill status={m.status || 'scheduled'} />
                                    </LiquidRow>
                                ))
                            )}
                        </Box>
                    </GlassPanel>

                    {/* top companies */}
                    <GlassPanel accent={palette.amber} delay={0.15}>
                        <PanelHead
                            icon={<TrophyIcon />}
                            title="Top companies"
                            accent={palette.amber}
                            chip="by meetings"
                            action="All"
                            onAction={() => navigate('/companies')}
                            dense
                        />
                        <Box sx={{ flex: 1, maxHeight: 330, overflowY: 'auto', pr: 0.5, mr: -0.5 }}>
                            {loading ? (
                                Array.from({ length: 4 }).map((_, i) => (
                                    <Skeleton key={i} variant="rounded" height={54} sx={{ mb: 1, borderRadius: '16px', bgcolor: palette.hairline }} />
                                ))
                            ) : topCompanies.length === 0 ? (
                                <EmptyState icon={<BusinessIcon />} text="No meeting participation yet" />
                            ) : (
                                topCompanies.map((c, i) => {
                                    const accent = ACCENTS[i % ACCENTS.length];
                                    return (
                                        <LiquidRow key={c.id} accent={accent} index={i} onClick={() => navigate(`/companies/${c.id}`)}>
                                            <Box
                                                sx={{
                                                    width: 28, height: 28, flexShrink: 0, borderRadius: '10px',
                                                    display: 'grid', placeItems: 'center', color: '#fff',
                                                    fontFamily: fontMono, fontSize: '0.68rem', fontWeight: 700,
                                                    background: `linear-gradient(150deg, ${accent}, ${rgba(accent, 0.72)})`,
                                                    boxShadow: `0 6px 14px -6px ${rgba(accent, 0.9)}`,
                                                }}
                                            >
                                                {i + 1}
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: palette.ink }} noWrap>
                                                    {c.name}
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.66rem', color: palette.inkLight }} noWrap>
                                                    {c.type}
                                                </Typography>
                                            </Box>
                                            <Box
                                                sx={{
                                                    px: 1, py: 0.3, borderRadius: '9px', flexShrink: 0,
                                                    fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 700, color: accent,
                                                    bgcolor: rgba(accent, 0.1), border: `1px solid ${rgba(accent, 0.18)}`,
                                                }}
                                            >
                                                {c.meetings}
                                            </Box>
                                        </LiquidRow>
                                    );
                                })
                            )}
                        </Box>
                    </GlassPanel>
                </Box>

                {/* ── sponsorships ─────────────────────────────────────────── */}
                <GlassPanel accent={palette.amber} delay={0.05} lift={false}>
                    <PanelHead
                        icon={<StorefrontIcon />}
                        title="Recent sponsorships"
                        accent={palette.amber}
                        chip={`${compact(live.revenue)} collected`}
                        action="View all"
                        onAction={() => navigate('/dashboard/sponsorships')}
                    />
                    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' } }}>
                        {loading ? (
                            Array.from({ length: 3 }).map((_, i) => (
                                <Skeleton key={i} variant="rounded" height={132} sx={{ borderRadius: '18px', bgcolor: palette.hairline }} />
                            ))
                        ) : sponsorships.length === 0 ? (
                            <Box sx={{ gridColumn: '1 / -1' }}>
                                <EmptyState icon={<StorefrontIcon />} text="No sponsorship packages yet" />
                            </Box>
                        ) : (
                            sponsorships.map((s, i) => (
                                <motion.div
                                    key={s.id}
                                    initial={{ opacity: 0, scale: 0.97 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.4, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    <Box
                                        onClick={() => navigate(`/dashboard/sponsorships/${s.id}`)}
                                        sx={{
                                            p: 2, borderRadius: '18px', cursor: 'pointer', height: '100%',
                                            background: `linear-gradient(140deg, ${rgba('#FFFFFF', 0.78)}, ${rgba(palette.amber, 0.08)})`,
                                            border: `1px solid ${rgba('#FFFFFF', 0.7)}`,
                                            borderLeft: `3px solid ${palette.amber}`,
                                            boxShadow: `inset 0 1px 0 ${rgba('#FFFFFF', 0.9)}`,
                                            transition: 'transform .4s cubic-bezier(.22,1,.36,1), box-shadow .4s ease',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: `0 20px 40px -20px ${rgba(palette.amber, 0.9)}`,
                                            },
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                            <Avatar
                                                variant="rounded"
                                                sx={{
                                                    width: 40, height: 40, borderRadius: '13px', flexShrink: 0,
                                                    color: '#fff',
                                                    background: `linear-gradient(150deg, ${palette.amber}, ${palette.amberGlow})`,
                                                    boxShadow: `0 8px 18px -8px ${rgba(palette.amber, 0.9)}`,
                                                }}
                                            >
                                                <StorefrontIcon sx={{ fontSize: 20 }} />
                                            </Avatar>
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: palette.ink }} noWrap>
                                                    {s.packageType || 'Sponsorship'} package
                                                </Typography>
                                                <Typography sx={{ fontSize: '0.76rem', color: palette.inkSoft }} noWrap>
                                                    {s.companyName}
                                                </Typography>
                                                <Box sx={{ display: 'flex', gap: 0.6, mt: 1, flexWrap: 'wrap' }}>
                                                    <Box
                                                        sx={{
                                                            px: 0.9, py: 0.28, borderRadius: '8px',
                                                            fontFamily: fontMono, fontSize: '0.6rem', fontWeight: 700,
                                                            color: palette.amber, bgcolor: rgba(palette.amber, 0.12),
                                                            border: `1px solid ${rgba(palette.amber, 0.22)}`,
                                                        }}
                                                    >
                                                        ${compact(s.amount || 0)}
                                                    </Box>
                                                    <StatusPill status={s.isActive ? 'active' : 'inactive'} />
                                                    {s.paymentStatus && <StatusPill status={s.paymentStatus} />}
                                                </Box>
                                                {(s.startDate || s.endDate) && (
                                                    <Typography sx={{ mt: 1, fontFamily: fontMono, fontSize: '0.6rem', color: palette.inkFaint }}>
                                                        {formatDateTime(s.startDate)} → {formatDateTime(s.endDate)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>
                                </motion.div>
                            ))
                        )}
                    </Box>
                </GlassPanel>
            </Container>

            {isAdmin && <FloatingSupportButton onOpen={() => navigate('/admin/support')} />}

            <NotificationComposer
                open={composerOpen}
                onClose={() => setComposerOpen(false)}
                companies={allCompanies}
                author={{ name: userData?.displayName || 'Admin', uid: currentUser?.uid || null }}
            />

            <style>{`
                @keyframes tfPulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50%      { opacity: .45; transform: scale(.82); }
                }
                @keyframes tfRule {
                    0%, 100% { opacity: .7; transform: scaleX(1); }
                    50%      { opacity: 1;  transform: scaleX(1.35); }
                }
                @keyframes tfSpin { to { transform: rotate(360deg); } }
                @keyframes tfGrain {
                    0%   { transform: translate(0, 0); }
                    25%  { transform: translate(-2%, 1%); }
                    50%  { transform: translate(1%, -2%); }
                    75%  { transform: translate(-1%, -1%); }
                    100% { transform: translate(0, 0); }
                }
                * { scrollbar-width: thin; scrollbar-color: ${rgba(palette.ink, 0.16)} transparent; }
                *::-webkit-scrollbar { width: 6px; height: 6px; }
                *::-webkit-scrollbar-track { background: transparent; }
                *::-webkit-scrollbar-thumb {
                    background: ${rgba(palette.ink, 0.14)};
                    border-radius: 999px;
                }
                *::-webkit-scrollbar-thumb:hover { background: ${rgba(palette.ink, 0.26)}; }
                @media (prefers-reduced-motion: reduce) {
                    *, *::before, *::after {
                        animation-duration: .001ms !important;
                        animation-iteration-count: 1 !important;
                        transition-duration: .001ms !important;
                    }
                }
            `}</style>
        </Box>
    );
};

export default Dashboard;